<template>
  <div class="comment-main">
    <div class="comment-item" v-show="showMainCommentBox">
      <div class="box">
        <div class="avatar-wrapper">
          <el-popover
            placement="bottom-start"
            width="280"
            trigger="click"
            v-model="showNicknameSelector"
            popper-class="nickname-popover"
            @show="ensureCommentSchoolOptions"
          >
            <!--
              列表来自 identityOptions（身份 × 学校 的叉乘），不是原始的 nicknameList：
              实名和匿名身份都会按教育经历展开成「· A校」「· B校」多项，再各加一项「· 不显示学校」。
              同校帖下只会有本帖所属学校那一项（见 commentSchoolChoices）。
            -->
            <div class="nickname-list">
              <div 
                v-for="nick in identityOptions" 
                :key="nick.key"
                class="nickname-item hand-style"
                :class="{ 'active': currentIdentityKey === nick.key }"
                @click="selectNickname(nick)"
              >
                <smart-img mode="avatar" :src="nick.avatar" :alt="nick.name" class="nickname-avatar" />
                <div class="nickname-info">
                  <div class="nickname-name">{{ nick.name }}</div>
                  <div class="nickname-type" :class="nick.nickType === 1 ? 'is-anonymous' : 'is-real'">{{ identityTypeText(nick) }}</div>
                </div>
                <i v-if="currentIdentityKey === nick.key" class="el-icon-check"></i>
              </div>
            </div>
            <div class="avatar hand-style" slot="reference">
              <!--
                原来这里是三个 img 分支，最后一个游客兜底指向 http://img.shiyit.com/touristAvatar.png
                —— 该域名已失效（DNS/连接都不通），实际就是一张裂图。
                现在合成一个 <smart-img>：取不到头像时自动用内置的内联 SVG 占位。
              -->
              <smart-img
                mode="avatar"
                :src="(selectedNickname && selectedNickname.avatar) || (user && user.avatar) || ''"
                alt="我的头像"
                eager
              />
              <div class="avatar-badge">
                <i class="el-icon-caret-bottom"></i>
              </div>
            </div>
          </el-popover>
        </div>
        <div class="ml-3">
          <div data-v-0089e256="" class="comment-input">
            <!--
              输入框里的 @昵称 要显示成蓝色（App 端同款做法，见 pp-front-uniapp 的 panel-textarea-mirror）。

              textarea 是纯文本控件，没法只给其中一段文字上色，只能用「镜像层」这个通用手法：
              在它上面盖一层盒模型完全一致的 div，把正文按 @ 切段渲染、@ 段染蓝；
              底下的 textarea 文字设成透明，只负责光标、选区和输入本身。

              镜像盖在上面而不是垫在下面：textarea 聚焦时背景会变成不透明的白色
              （见 .el-textarea__inner:focus），垫在下面会被整块盖掉。
              盖在上面的代价是必须放掉指针事件，否则点不进输入框、也拖选不了。

              只在正文里真的有 @ 时才渲染镜像 —— 没有 @ 的评论占绝大多数，
              那种情况下 textarea 完全保持原样，零风险。
            -->
            <div class="comment-textarea-wrap">
              <!-- 片段必须写在同一行、标签之间不留空白：镜像是 pre-wrap，
                   换行和缩进会变成可见的空格，镜像就比真实文字宽出一截，
                   后面所有文字连光标都会往右偏，而且偏移会累积 -->
              <div v-if="showInputHighlight" ref="inputMirror" class="comment-input-mirror" aria-hidden="true"><template v-for="(seg, segIndex) in inputSegments"><span v-if="seg.type === 'at'" :key="`mirror-at-${segIndex}`" class="mirror-at-text">{{ seg.text }}</span><span v-else :key="`mirror-txt-${segIndex}`">{{ seg.text }}</span></template></div>
              <el-input
                ref="editableDiv"
                type="textarea"
                v-model="commentContent"
                placeholder="说点什么呢"
                :rows="4"
                :maxlength="500"
                show-word-limit
                :class="['comment-textarea', { 'is-highlighting': showInputHighlight }]"
                @input="onContentInput"
                @keydown.native="onTextareaKeydown"
              ></el-input>

              <!--
                光标探针：用来算出正文里那个 "@" 画在输入框的哪个位置。

                textarea 没有任何 API 能问出"第 n 个字符在哪"，通行做法是拿一个
                盒模型完全一致的元素把 "@" 之前的正文原样排一遍，末尾跟一个零宽标记，
                标记的 offsetLeft / offsetTop 就是 "@" 的坐标。
                它和 textarea 共用 comment-input-box mixin，字号、行高、内边距、
                折行规则天然一致 —— 这是量得准的前提，任何一项只改一边都会让面板跟偏。

                只在「正文里有一段正在输入的 @」时渲染，平时不存在。
                写成一行、标签之间不留空白：这一层是 pre-wrap，
                换行和缩进都会被当成真实字符参与排版，量出来的坐标就错了。
              -->
              <div v-if="atTriggerIndex >= 0" class="comment-caret-probe" aria-hidden="true">{{ commentContent.slice(0, atTriggerIndex) }}<span ref="caretProbeMark" class="comment-caret-mark"></span></div>

              <!--
                选人面板的定位锚点：一个 0×0 的空元素，由 updateMentionAnchor 挪到
                "@" 所在那一行的下沿 —— 面板因此紧跟在 "@" 后面展开，而不是挂在
                输入框底部（"@" 在第一行时，挂框底会离它很远）。
                放在行下沿而不是行内：查询词 "@张…" 和 "@" 同一行、在面板上方，
                始终看得见自己在打什么。Slack、牛客都是这个位置关系。

                锚点本身点不到（0×0 且没有内容），所以面板只会由 onContentInput /
                onMentionButtonClick 通过 open() 打开
              -->
              <MentionPicker
                ref="mentionPicker"
                class="mention-anchor"
                :style="mentionAnchorStyle"
                :selected-ids="selectedAtUserIds"
                @select="handleSelectAtUser"
              >
                <span></span>
              </MentionPicker>
            </div>
          </div>
          <!-- 已选配图的缩略图。选的时候只压缩不上传，点「发布」才传 COS，
               理由见 mixins/commentImageMixin.js。没选图时整块不渲染。 -->
          <div v-if="commentSticker" class="comment-sticker-preview">
            <img :src="commentSticker.url" alt="表情" />
            <div class="comment-upload-remove" @click="commentSticker = null">
              <i class="el-icon-close"></i>
            </div>
          </div>
          <div v-if="commentImageList.length" class="comment-upload-list">
            <div class="comment-upload-item" v-for="(url, index) in commentImageList" :key="index">
              <!-- 本地 blob 预览地址，不能走 CDN 缩略图（那是给远程图用的） -->
              <img :src="url" alt="评论配图" />
              <div class="comment-upload-remove" @click="removeCommentImage(index)">
                <i class="el-icon-close"></i>
              </div>
            </div>
          </div>

          <div class="comment-btn">
            <Emoji @chooseEmoji="handleChooseEmoji">
              <span class="emoji-btn hand-style">
                <emoji-icon />
              </span>
            </Emoji>
            <StickerPicker
              ref="stickerPicker"
              :mine="stickerMine"
              :packs="stickerPacks"
              :active-pack="stickerToolbarPack"
              @open="onStickerPickerOpen"
              @select="selectCommentSticker"
              @upload="uploadCommentSticker"
              @remove="removeCommentSticker"
              @pin="pinCommentSticker"
              @add-recommend="addRecommendSticker"
            >
              <span class="emoji-btn sticker-entry-btn hand-style" title="斗图">
                <sticker-icon />
              </span>
            </StickerPicker>
            <span
              v-for="pack in stickerPacks"
              :key="pack.id"
              class="emoji-btn sticker-pack-btn hand-style"
              :title="pack.name"
              @click.stop="openStickerPackFromToolbar(pack.id, $event)"
            >
              <img :src="pack.cover" alt="" />
            </span>

            <!-- @ 提及。被 @ 的人会收到一条站内通知。
                 按钮只负责往光标处插一个 "@"，面板由 onContentInput 统一顶出来 ——
                 和用户手打 "@" 走的是同一条路径，见 onMentionButtonClick -->
            <span
              class="mention-btn hand-style"
              title="提及好友"
              @click="onMentionButtonClick"
            >@</span>

            <!-- 配图。选满就不再渲染上传入口，缩略图那一排本身已经说明了状态 -->
            <el-upload
              v-if="commentImageList.length < commentImageMaxCount"
              class="comment-image-upload"
              :show-file-list="false"
              action="#"
              :http-request="handleCommentImageUpload"
              :before-upload="beforeCommentImageUpload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            >
              <span class="image-btn hand-style" :title="`添加图片（最多${commentImageMaxCount}张）`">
                <i class="el-icon-picture-outline"></i>
              </span>
            </el-upload>

            <el-button
              @click="addComment"
              :loading="submitting"
              :class="['upload-btn', 'v-comment-btn', 'hand-style', { 'has-content': hasContent }]"
            >
              发布
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <ul class="commentwrap">
      <div class="comment-wrp">
        <li
          class="ul-item"
          :ref="'commentItem' + item.id"
          v-for="item in commentList"
          :key="item.id"
        >
          <!-- 父评论内容 -->
          <div class="comment" :ref="'commentContent' + item.id">
            <div class="main">
              <div class="profile">
                <a
                  :href="item.nickType === $enumType.isAnonymous.YES ? 'javascript:void(0)' : `/user/${item.userId}`"
                  :target="item.nickType === $enumType.isAnonymous.YES ? '_self' : '_blank'"
                  :class="['avatar-link', { 'anonymous': item.nickType === $enumType.isAnonymous.YES }]"
                >
                  <!-- 原 el-avatar size="medium"（36px），换成 <smart-img> 走 CDN 缩略图 -->
                  <smart-img mode="avatar" :src="item.avatar" :alt="item.nickname" class="comment-avatar" />
                </a>
              </div>
              <div class="commentinfo">
                <section class="commeta">
                  <div class="left">
                    <h4 class="author">
                      <a
                        :href="item.nickType === $enumType.isAnonymous.YES ? 'javascript:void(0)' : `/user/${item.userId}`"
                        :target="item.nickType === $enumType.isAnonymous.YES ? '_self' : '_blank'"
                        :class="['author-link', { 'anonymous': item.nickType === $enumType.isAnonymous.YES }]"
                      >
                        {{ item.nickname }}
                      </a>
                      <span class="nick-type" v-if="item.nickType === $enumType.isAnonymous.YES">
                        <svg-icon icon-class="nickred" size="18" />
                      </span>
                    </h4>
                    <div class="school-name" v-if="item.schoolName">
                      {{ item.schoolName }}
                      <!-- 匿名身份不展示学校认证角标：角标是实名背书，挂在匿名马甲上等于泄露身份 -->
                      <verified-badge
                        v-if="item.nickType !== $enumType.isAnonymous.YES"
                        :verified="item.campusVerified"
                        :size="14"
                      />
                    </div>
                  </div>
                </section>
                <!-- v-if 挂在 .body 上而不是里面的 <p>：纯图评论（正文为空）时，
                     只藏 <p> 会留下一个空的 .body，而它带着 margin-bottom: 15px，
                     表现是昵称和配图之间凭空多出一段空白 -->
                <div class="body markdown-body" v-if="item.content">
                  <div class="markdown-content">
                    <!--
                      正文按「普通文本 / @提及」切成片段分别渲染，@ 片段是可点击的主页链接。
                      用元素而不是 v-html：昵称是用户可控内容，拼成 HTML 注入等于自找 XSS。
                      两个分支必须写在同一行、中间不留空白 —— .comment-text 是 pre-wrap，
                      标签之间的换行在 whitespace 策略变化时会变成可见的空格。
                    -->
                    <p class="comment-text"><template v-for="(seg, segIndex) in mentionSegments(item)"><a v-if="seg.type === 'at' && !seg.cancelled" :key="`at-${segIndex}`" class="at-text" :href="`/user/${seg.userId}`" target="_blank">{{ seg.text }}</a><span v-else :key="`txt-${segIndex}`">{{ seg.text }}</span></template></p>
                  </div>
                </div>
                <!--
                  评论配图。放在 .body 外面而不是 .markdown-content 里：那一层是
                  white-space: pre-line + word-break: break-all，是给正文准备的，
                  图片格子继承过去只会带来意外（标签之间的换行有机会变成可见空行）。
                  这里和 .comment-footer 一样自己带 margin-left 对齐头像右侧。

                  用 el-image 而不是 <smart-img>：需要点开看大图，
                  el-image 自带 preview-src-list 全屏预览器，换成裸 img 就得自己写一个。

                  两条硬约束（见 mixins/contentImageMixin.js）：
                    1) 格子里渲染缩略图，preview-src-list 一律传原图 —— 缩略图看细节不够；
                    2) 必须挂 @error 降级，CDN 缓存未命中时数据万象的现场处理会失败。
                -->
                <img
                  v-if="item.stickerUrl"
                  class="comment-sticker-img"
                  :src="item.stickerUrl"
                  alt="表情"
                />
                <div v-if="commentImages(item).length" class="comment-image-list">
                  <el-image
                    v-for="(img, imgIndex) in commentImages(item)"
                    :key="imgIndex"
                    class="comment-image"
                    :src="contentThumb(img)"
                    :preview-src-list="commentImages(item)"
                    :initial-index="imgIndex"
                    fit="cover"
                    @error="onContentThumbError(img)"
                  />
                </div>
                <div class="comment-footer">
                  <div class="info">
                    <time class="comment-time">{{ item.createTimeStr }}</time>
                    <span class="ipAddress">{{ item.ipSource }}</span>
                  </div>
                  <div class="comment-actions">
                    <a
                      :ref="'replyBtn' + item.id"
                      @click="replyComment(item, item.id, false)"
                      class="comment-reply-link hand-style"
                    >回复</a>
                    <a
                      @click="handleLike(item)"
                      class="comment-like-link hand-style"
                      :class="{ 'liked': item.hasCommentLike }"
                    >
                      <i class="iconfont icon-dianzan"></i>
                      <span v-if="item.commentLikeCount > 0">{{ item.commentLikeCount }}</span>
                    </a>
                    <!-- 更多：分享 / 私信 / 复制 / 添加到表情 / 举报 / 删除，对齐 App 端长按菜单 -->
                    <comment-action-menu
                      :comment="item"
                      @action="onCommentAction($event, item, null)"
                    />
                  </div>
                </div>
                <!-- 回复框 -->
                <Reply
                  :ref="'reply' + item.id"
                  :articleInfo="articleInfo"
                  :identityOptions="identityOptions"
                  :currentIdentityKey="currentIdentityKey"
                  :selectedNickname="selectedNickname"
                  :commentSchoolId="commentSchoolId"
                  :commentSchoolName="commentSchoolName || profileSchoolName"
                  @reloadReply="reloadReply"
                  @closeReply="handleCloseReply"
                  @selectNickname="selectNickname"
                  @identityPickerShow="ensureCommentSchoolOptions"
                />
              </div>
            </div>
          </div>

          <!-- 子评论区域。
               /parentList 现在每楼会带回 childRecords（前 2 条回复），
               所以折叠态也有内容可渲染 —— 用户不用先点一次「展开N条回复」
               才知道这一楼在聊什么。剩下的仍然是懒加载。 -->
          <div class="children" v-if="item.childCount > 0 || (item.children && item.children.length > 0)">
            <div class="comment-wrp">
              <!-- 查看更早的回复。
                   只在「从消息通知定位过来」时出现：那种情况服务端直接给的是目标回复
                   所在的那一页（比如楼内第 5 页），它前面几页还没加载。
                   手动展开永远从第 1 页开始，走不到这里。 -->
              <div
                v-if="item.childrenLoaded && item.childrenHasPrevious"
                class="load-more-replies hand-style"
                @click="loadPrevChildComments(item)"
              >
                <span v-if="!item.childrenLoading">
                  {{ item.childrenPreviousCount > 0 ? `查看更早的 ${item.childrenPreviousCount} 条回复` : '查看更早的回复' }}
                </span>
                <i v-else class="el-icon-loading"></i>
              </div>

              <!-- 子评论列表 -->
              <!-- key 必须用 id 而不是下标：「查看更早的回复」是往数组头部插入，
                   用下标做 key 会让 Vue 就地复用节点、把内容错位patch 到相邻元素上，
                   而定位高亮是直接写在 DOM 节点内联样式上的，会跟着留在错误的那条回复上。 -->
              <div
                class="ul-item"
                :ref="'commentItem' + childrenItem.id"
                v-for="childrenItem in visibleChildren(item)"
                :key="childrenItem.id"
              >
                <div class="comment" :ref="'commentContent' + childrenItem.id">
                  <div class="main">
                    <div class="profile">
                      <a
                        :href="childrenItem.nickType === $enumType.isAnonymous.YES ? 'javascript:void(0)' : `/user/${childrenItem.userId}`"
                        :target="childrenItem.nickType === $enumType.isAnonymous.YES ? '_self' : '_blank'"
                        :class="['avatar-link', { 'anonymous': childrenItem.nickType === $enumType.isAnonymous.YES }]"
                      >
                        <smart-img mode="avatar" :src="childrenItem.avatar" :alt="childrenItem.nickname" class="comment-avatar" />
                      </a>
                    </div>
                    <div class="commentinfo">
                      <section class="commeta">
                        <div class="left">
                          <h4 class="author">
                            <a
                              :href="childrenItem.nickType === $enumType.isAnonymous.YES ? 'javascript:void(0)' : `/user/${childrenItem.userId}`"
                              :target="childrenItem.nickType === $enumType.isAnonymous.YES ? '_self' : '_blank'"
                              :class="['author-link', { 'anonymous': childrenItem.nickType === $enumType.isAnonymous.YES }]"
                            >
                              {{ childrenItem.nickname }}
                            </a>
                            <span class="nick-type" v-if="childrenItem.nickType === $enumType.isAnonymous.YES">
                              <svg-icon icon-class="nickred" size="18" />
                            </span>
                            <!-- 3级以上显示回复人 -->
                            <span v-if="childrenItem.level >= 3 && childrenItem.replyNickname" class="reply-to">
                              <i class="el-icon-right"></i>
                              <a
                                :href="childrenItem.replyNickType === $enumType.isAnonymous.YES ? 'javascript:void(0)' : `/user/${childrenItem.replyUserId}`"
                                :target="childrenItem.replyNickType === $enumType.isAnonymous.YES ? '_self' : '_blank'"
                                :class="['reply-nickname', { 'anonymous': childrenItem.replyNickType === $enumType.isAnonymous.YES }]"
                              >
                                {{ childrenItem.replyNickname }}
                              </a>
                            </span>
                          </h4>
                          <div class="school-name" v-if="childrenItem.schoolName">
                            {{ childrenItem.schoolName }}
                            <!-- 匿名身份不展示学校认证角标：角标是实名背书，挂在匿名马甲上等于泄露身份 -->
                            <verified-badge
                              v-if="childrenItem.nickType !== $enumType.isAnonymous.YES"
                              :verified="childrenItem.campusVerified"
                              :size="14"
                            />
                          </div>
                        </div>
                      </section>
                      <!-- 同父评论：v-if 挂在 .body 上，纯图回复不留空的正文块 -->
                      <div class="body markdown-body" v-if="childrenItem.content">
                        <div class="markdown-content">
                          <!-- 同父评论：@ 片段渲染成主页链接，理由见上面那段注释 -->
                          <p class="comment-text"><template v-for="(seg, segIndex) in mentionSegments(childrenItem)"><a v-if="seg.type === 'at' && !seg.cancelled" :key="`at-${segIndex}`" class="at-text" :href="`/user/${seg.userId}`" target="_blank">{{ seg.text }}</a><span v-else :key="`txt-${segIndex}`">{{ seg.text }}</span></template></p>
                        </div>
                      </div>
                      <img
                        v-if="childrenItem.stickerUrl"
                        class="comment-sticker-img is-child"
                        :src="childrenItem.stickerUrl"
                        alt="表情"
                      />
                      <!-- 回复配图，位置和理由同父评论 -->
                      <div v-if="commentImages(childrenItem).length" class="comment-image-list is-child">
                        <el-image
                          v-for="(img, imgIndex) in commentImages(childrenItem)"
                          :key="imgIndex"
                          class="comment-image"
                          :src="contentThumb(img)"
                          :preview-src-list="commentImages(childrenItem)"
                          :initial-index="imgIndex"
                          fit="cover"
                          @error="onContentThumbError(img)"
                        />
                      </div>
                      <div class="comment-footer">
                        <div class="info">
                          <time class="comment-time">{{ childrenItem.createTimeStr }}</time>
                          <span class="ipAddress">{{ childrenItem.ipSource }}</span>
                        </div>
                        <div class="comment-actions">
                          <a
                            href="javascript:;"
                            :ref="'childrenBtn' + childrenItem.id"
                            @click="replyComment(childrenItem, item.id, true)"
                            class="comment-reply-link hand-style"
                          >回复</a>
                          <a
                            @click="handleLike(childrenItem)"
                            class="comment-like-link hand-style"
                            :class="{ 'liked': childrenItem.hasCommentLike }"
                          >
                            <i class="iconfont icon-dianzan"></i>
                            <span v-if="childrenItem.commentLikeCount > 0">{{ childrenItem.commentLikeCount }}</span>
                          </a>
                          <!-- 回复同样有更多菜单；传父评论 id，删除后要从这一楼里摘掉它 -->
                          <comment-action-menu
                            :comment="childrenItem"
                            @action="onCommentAction($event, childrenItem, item.id)"
                          />
                        </div>
                      </div>
                      <!-- 回复框 -->
                      <Reply
                        :ref="'replys' + childrenItem.id"
                        :articleInfo="articleInfo"
                        :identityOptions="identityOptions"
                        :currentIdentityKey="currentIdentityKey"
                        :selectedNickname="selectedNickname"
                        :commentSchoolId="commentSchoolId"
                        :commentSchoolName="commentSchoolName || profileSchoolName"
                        @reloadReply="reloadReply"
                        @closeReply="handleCloseReply"
                        @selectNickname="selectNickname"
                        @identityPickerShow="ensureCommentSchoolOptions"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- 展开剩余回复。
                   放在预览的两条下面（而不是上面）：折叠态的阅读顺序是
                   「评论 → 前两条回复 → 还有 X 条」，和移动端一致；
                   放上面会变成「还有 X 条 → 前两条回复」，读到按钮时
                   用户还不知道下面已经有回复了。

                   X 是「被折叠起来的条数」而不是回复总数：预览已经露出两条了，
                   再写总数会让人以为点开还有那么多，点开却只多出几条。 -->
              <div
                v-if="!item.childrenLoaded && hiddenChildCount(item) > 0"
                class="expand-replies hand-style"
                @click="loadChildComments(item)"
              >
                <i class="el-icon-chat-dot-round"></i>
                展开 {{ hiddenChildCount(item) }} 条回复
                <i v-if="item.childrenLoading" class="el-icon-loading"></i>
              </div>

              <!-- 加载更多子评论 -->
              <div 
                v-if="item.childrenLoaded && item.childrenHasMore" 
                class="load-more-replies hand-style"
                @click="loadMoreChildComments(item)"
              >
                <span v-if="!item.childrenLoading">加载更多回复</span>
                <i v-else class="el-icon-loading"></i>
              </div>
            </div>
          </div>
        </li>

        <!-- 加载提示 -->
        <div v-if="loading" class="comment-status-tip">
          <i class="el-icon-loading"></i> 加载中...
        </div>
        <div v-else-if="!loading && commentList.length === 0" class="comment-status-tip empty-tip">
          暂无评论，快来抢沙发~
        </div>
        <div v-else-if="!hasMore && commentList.length > 0" class="comment-status-tip no-more-tip">
          没有更多评论了
        </div>
      </div>
    </ul>

    <!-- 评论「更多」菜单用到的三个弹窗，整个评论区共用一份，不跟着每条评论各建一个 -->
    <report-dialog
      :visible.sync="commentReportVisible"
      target-type="comment"
      :comment-id="commentReportId"
      :target-name="commentReportQuote"
    />
    <comment-share-dialog
      :visible.sync="commentShareVisible"
      :comment="commentShareTarget"
      :article-id="articleInfo && articleInfo.id"
    />
    <comment-dm-dialog
      :visible.sync="commentDmVisible"
      :user="commentDmTarget"
    />
  </div>
</template>

<script>
import { userFetch } from '@/api'
// 评论身份要按学校展开，学校来源就是教育经历（见 ensureCommentSchoolOptions）
import { getEducationList } from '@/api/system'
import { browserMatch } from '@/utils/index'
import { getToken } from '@/utils/cookieUtil'
import Reply from './Reply.vue'
import Emoji from '@/components/emoji'
import EmojiIcon from '@/components/EmojiIcon'
import StickerIcon from '@/components/StickerIcon'
import StickerPicker from '@/components/sticker'
import stickerMixin from '@/mixins/stickerMixin'
import { uploadImageToCos } from '@/utils/cosUpload'
import MentionPicker from './MentionPicker.vue'
import CommentActionMenu from './CommentActionMenu.vue'
import CommentShareDialog from './CommentShareDialog.vue'
import CommentDmDialog from './CommentDmDialog.vue'
import ReportDialog from '@/components/report-dialog/index.vue'
import { copyText } from '@/utils/clipboard'
import {
  canChatWithCommentUser,
  isAnonymousComment,
  isCancelledCommentUser,
  collectableStickerUrl,
  commentSummary
} from './commentActions'
import contentImageMixin from '@/mixins/contentImageMixin'
import commentImageMixin from '@/mixins/commentImageMixin'
import { isAuditPublished, getAuditBadge } from '@/constant'
import {
  MAX_AT_USER_COUNT,
  parseMentionSegments,
  detectMentionQuery,
  insertMention,
  insertMentionAt,
  findMentionTokenAt,
  syncAtUsers,
  buildAtUserIds
} from '@/utils/mention'

// 与 el-input 上的 :maxlength 保持一致
const COMMENT_MAX_LENGTH = 500

// 折叠态每楼默认露出几条回复。
//
// 必须和后端 ApiCommentServiceImpl.PARENT_LIST_CHILD_PREVIEW_SIZE 一致：
// /parentList 每楼预取的就是这么多条。这里再截一次是为了兜住
// 「children 里的条数超过预取值」的情况（目前只有锚点楼可能出现），
// 免得折叠态一次露出一整页。
//
// 这个值和下面的 CHILD_COMMENT_PAGE_SIZE 没有关系，不要混为一谈：
// 预览只是「先给你看两条」，点展开时仍然从楼内第 1 页（10 条）重新拉一整页覆盖掉它。
const CHILD_PREVIEW_SIZE = 2

// 楼内回复的每页条数。
//
// 必须和后端 ApiCommentServiceImpl.LOCATE_CHILD_PAGE_SIZE 保持一致：
// /v1/comment/locate 返回的 childPageNo 是按那个值算出来的，前端把它当作
// 「已加载到第几页」的游标继续往前往后翻。两边不一致，偏移就会错位 ——
// 表现是接着翻时重复出现看过的回复、同时漏掉一部分。
const CHILD_COMMENT_PAGE_SIZE = 10

export default {
  components: {
    Reply,
    Emoji,
    EmojiIcon,
    StickerIcon,
    MentionPicker,
    StickerPicker,
    CommentActionMenu,
    CommentShareDialog,
    CommentDmDialog,
    ReportDialog
  },
  // contentImageMixin：评论配图走 CDN 缩略图 + 失败退回原图（contentThumb / onContentThumbError）
  // commentImageMixin：发评论时的选图 / 预览 / 上传
  mixins: [contentImageMixin, commentImageMixin, stickerMixin],
  props: {
    articleInfo: {
      type: Object,
      default: () => ({})
    },
    // 要定位并高亮的评论 id（来自消息中心 / 个人中心「我的评论」）。
    //
    // 类型放开成 [String, Number]：detail.vue 传的是 String(...)，
    // 而 notice/index.vue 和 ArticleContent.vue 直接把 JSONBig 解析出来的
    // article.commentId 传进来（可能是 Number），声明成纯 String 会刷 prop 类型告警。
    // 组件内部一律先 String() 再用。
    commentId: {
      type: [String, Number],
      default: ''
    },
    // 触底加载监听哪个滚动容器。不传时按原来的口径：详情弹窗内部滚动用弹窗自身，独立页退化为窗口。
    // 视频播放器的右侧面板 / 评论半屏面板各有自己的滚动容器，传一个返回该元素的函数（或选择器）
    scrollContainer: {
      type: [Function, String],
      default: null
    }
  },
  watch: {
    // immediate：视频播放器创建评论组件时帖子数据已经齐了，id 不会再变，不立即执行就永远不会去拉评论。
    // 详情弹窗里 articleInfo 一开始是没有 id 的占位对象，立即执行时 val 为空，行为不变
    'articleInfo.id': {
      handler (val) {
        if (val) {
          this.getParentComments()
        }
      },
      immediate: true
    },
    // 这里原来是 $nextTick 直接 scrollToComment(val)，两个问题：
    // 1) 首次触发（immediate）时评论列表还是空的，必然什么都找不到；
    // 2) 就算列表已加载，目标是条回复时 ref 也不存在 —— 回复要等用户点
    //    「展开N条回复」才渲染出来，所以「有人回复了你的评论」这类通知点进来永远定位失败。
    // 改成走 locate：让服务端把目标所在的整楼取回来置顶展开，再滚过去。
    commentId: {
      handler (val) {
        // 弹窗是全局单例，关闭时会把 articleId / commentId 都清空（见 article-dialog）。
        // 这里必须跟着复位，否则「点开评论A → 关闭 → 再点开同一条评论A」时，
        // 去重标记还停在 A 上，第二次就不会再定位了。
        // anchorRootId 一并清掉：留着上一篇帖子的楼号，会把下一篇里恰好同 id 的
        // 评论莫名置顶（正常不会撞 id，但这个状态本来就该随弹窗一起结束）。
        if (!val) {
          this._locatingFor = ''
          this.anchorRootId = ''
          return
        }
        this.locateAndHighlight()
      },
      immediate: true
    },
    // 正文一变就把 @ 高亮镜像滚回和 textarea 一致的位置。
    // 放在 watch 里而不是散在每个改写正文的地方（键盘输入、插表情、插 @、发布后清空），
    // 漏掉任何一处都会看到镜像和真实文字错开的双影
    commentContent () {
      this.$nextTick(this.syncMirrorScroll)
    }
  },
  data () {
    return {
      commentContent: "",
      commentSticker: null,
      // 发布中标记：防止连点造成重复评论
      submitting: false,
      pageData: {
        pageNo: 1,
        pageSize: 10,
      },
      commentList: [],
      // 「锚点楼」置顶的那条父评论 id（字符串）。
      //
      // 带 commentId 进来时，先调 /v1/comment/locate 取回目标所在的整楼，
      // 置顶插到列表最前面并默认展开 —— 这样即使目标是条回复，它的节点也一定存在，
      // 定位不再依赖「目标刚好在第一页父评论里」这个前提。
      // 记下 id 是为了后续翻页去重：这一楼在它本来的页码上还会再回来一次。
      anchorRootId: '',
      pages: 0,
      lastCommentId: null,
      loading: false,
      hasMore: true,
      nicknameList: [],
      selectedNickname: null,
      showNicknameSelector: false,
      // 这条评论要挂在哪所学校名下。和 selectedNickname 分开存：
      // 切到「不显示学校」时界面上不显示学校，但这个选择要留着，切回带学校的选项还是它。
      commentSchoolId: '',
      commentSchoolName: '',
      // 可选学校（教育经历）。刻意懒加载 —— 只有用户真的展开身份下拉才去拉。
      // 每次打开帖子都请求一遍不值得：不选学校时服务端会按「资料展示的那段经历」兜底，
      // 结果和默认值完全一致（同校帖更是服务端强制用本帖学校）。
      commentSchoolOptions: [],
      commentSchoolOptionsLoaded: false,
      commentSchoolOptionsLoading: false,
      showMainCommentBox: true, // 控制主评论输入框的显示/隐藏
      // 已选的被 @ 用户 [{ userId, nickname }]。
      // 提交前会用正文再校准一遍（syncAtUsers），因为用户可能选完人又把
      // "@某人" 从正文里删掉了，那种情况不该再给他发 @ 通知
      selectedAtUsers: [],
      // 用户打 "@" 唤起面板时，那个 '@' 在正文里的下标。
      // 选中后要把 "@已输入的搜索词" 整段替换掉；没有正在输入的 @ 时为 -1。
      // 它同时也是光标探针的开关和量测起点（见 updateMentionAnchor）
      atTriggerIndex: -1,
      // 选人面板锚点的内联定位，形如 { left: '48px', top: '34px' }。
      // 由 updateMentionAnchor 按 "@" 的实际像素位置算出来，null 表示还没算过
      mentionAnchorStyle: null,

      // ===== 评论「更多」菜单 =====
      // 举报评论：弹窗整个评论区共用一个，打开前把目标评论写进来
      commentReportVisible: false,
      commentReportId: '',
      commentReportQuote: '',
      // 分享给好友
      commentShareVisible: false,
      commentShareTarget: null,
      // 私信评论人 { userId, nickname }
      commentDmVisible: false,
      commentDmTarget: null,
      // 正在删除的评论 id：确认框弹着或请求在路上时，挡住对同一条（或另一条）的重复删除
      deletingCommentId: '',
    }
  },
  computed: {
    // 传给 MentionPicker 用于打勾和挡重复选择
    selectedAtUserIds () {
      return this.selectedAtUsers.map(item => item.userId)
    },

    // 输入框正文切成「普通文本 / @提及」片段，给高亮镜像层染色用。
    // 和评论列表渲染用的是同一个 parseMentionSegments，所以「输入时看到的蓝色范围」
    // 和「发出去之后看到的蓝色范围」必然一致，不会出现输入时是蓝的、发出来变黑了。
    //
    // 网页端不需要 App 端那个「空格换成 U+00A0」的处理：镜像层是 <span> + pre-wrap，
    // 普通空格不会被折叠掉，宽度和 textarea 里的完全一样
    inputSegments () {
      return parseMentionSegments(this.commentContent, this.selectedAtUsers)
    },

    // 是否启用输入框高亮镜像：正文里真的有 @ 提及才开。
    // 没有 @ 的评论占绝大多数，那种情况让 textarea 保持原样（文字不透明、滚动条照常）
    showInputHighlight () {
      return this.inputSegments.some(seg => seg.type === 'at')
    },

    // 「发布」按钮的高亮态。只发图不发字也算有内容，
    // 否则用户选好图之后按钮还是灰的，看起来像发不出去
    hasContent () {
      return this.commentContent.trim().length > 0 || this.hasCommentImages || !!this.commentSticker
    },
    // 直接读 store：登录框就在同一个页面里，登录成功后这里要能立刻拿到新用户信息。
    // 原来在 data 里取一次快照，未登录时进详情页登录完，评论仍会用旧的空对象。
    user () {
      const info = this.$store.state.userStore.userInfo
      return info && info.id ? info : null
    },

    // ===== 评论身份 × 学校 =====
    //
    // 本帖锁定的学校。同校帖下评论露出的学校必须是本帖所属的那一所 ——
    // 在 A 校的同校帖里冒出个 B 校身份是错的。服务端 insertComment 也会强制收敛到
    // 帖子的 school_id，这里只是别让用户先选了个最后不生效的值。
    lockedCommentSchoolId () {
      if (Number(this.articleInfo?.type) !== 1) return ''
      return this.articleInfo?.schoolId ? String(this.articleInfo.schoolId) : ''
    },
    // 实名身份可选的学校：普通帖是全部教育经历，同校帖只留本帖那一所。
    // 同校帖而评论者并不是该校成员（从分享链接进来的）时这里是空的，
    // 于是下面不按学校展开，服务端也会回退到他自己的资料学校
    commentSchoolChoices () {
      if (!this.lockedCommentSchoolId) return this.commentSchoolOptions
      return this.commentSchoolOptions.filter(
        item => String(item.schoolId) === this.lockedCommentSchoolId
      )
    },
    // 不按学校展开时实名身份显示的学校名。服务端此时用的是「资料展示的那段经历」，
    // 也就是 userInfo.school，两边口径一致
    profileSchoolName () {
      return (this.user && this.user.school) || ''
    },
    // 身份 × 学校 的叉乘，身份下拉直接渲染它。
    // 身份和学校是两个正交的维度（身份挂在 b_user.parent_user_id 上，学校挂在
    // b_user_education.parent_user_id 上，实名和匿名共用同一组经历），
    // 用户真正要选的是「用哪个身份 + 挂哪所学校 / 不显示学校」，所以展开成一维列表。
    // 实名和匿名一视同仁，口径同 App 端：每个身份按学校展开（没展开时只有一项），
    // 再加一项「不显示学校」—— 选它发的评论不挂任何学校（服务端写 b_comment.hide_author_school）。
    identityOptions () {
      const list = Array.isArray(this.nicknameList) ? this.nicknameList : []
      const schools = this.commentSchoolChoices
      const options = []
      list.forEach(item => {
        const base = {
          id: item.id,
          name: item.name,
          avatar: item.avatar,
          nickType: item.nickType
        }
        if (!schools.length) {
          options.push({ ...base, key: `${item.id}:`, hideSchool: false, schoolId: '', schoolName: this.profileSchoolName })
        } else {
          schools.forEach(school => {
            const schoolId = String(school.schoolId)
            options.push({
              ...base,
              key: `${item.id}:${schoolId}`,
              hideSchool: false,
              schoolId,
              schoolName: school.schoolName || ''
            })
          })
        }
        // key 用 none 区分：学校 id 都是数字，不会和它撞上
        options.push({ ...base, key: `${item.id}:none`, hideSchool: true, schoolId: '', schoolName: '' })
      })
      // 选中项排到最前面：下拉最高 300px（见 .nickname-list），
      // 身份 × 学校 展开后选项可能有十几条，选中项排在后面时一打开看不见，
      // 用户得先滚一下才知道自己现在用的是哪个身份。
      // 选完下拉就收了（selectNickname），所以重排不会在用户眼前跳。
      const selectedIndex = options.findIndex(item => item.key === this.currentIdentityKey)
      if (selectedIndex > 0) {
        options.unshift(options.splice(selectedIndex, 1)[0])
      }
      return options
    },
    // 选中项标识，拼法必须和 identityOptions 里的 key 完全对齐，否则对勾会错位或消失。
    // 学校只在真的按学校展开时才参与比较 —— 没展开时选项 key 里没有学校段，
    // 这时拿 commentSchoolId 去拼会跟任何一项都对不上。
    // 「不显示学校」那一项的 key 固定是 `${id}:none`。
    currentIdentityKey () {
      const id = this.selectedNickname ? this.selectedNickname.id : ''
      if (this.selectedNickname && this.selectedNickname.hideSchool) return `${id}:none`
      const expanded = this.commentSchoolChoices.length > 0
      const schoolPart = expanded ? (this.commentSchoolId || '') : ''
      return `${id}:${schoolPart}`
    }
  },
  mounted () {
    this.$nextTick(() => {
      // 滚动容器：调用方指定的优先；否则弹窗形态用弹窗自身，独立页（/article/:id 分享落地页）退化为窗口
      this._scrollTarget = this.resolveScrollTarget()
      // 滚动回调里要读 scrollHeight / clientHeight，属于强制同步布局；
      // 不加节流的话每一帧滚动都会触发一次 reflow，长评论列表下掉帧明显。
      this._scrollTarget.addEventListener('scroll', this.onScrollThrottled, { passive: true })
      // @ 高亮镜像要跟着输入框的内部滚动走，见 syncMirrorScroll
      this.bindMirrorScroll()
    })
    this.getNicknameList()
  },
  beforeDestroy () {
    if (this._scrollTarget) {
      this._scrollTarget.removeEventListener('scroll', this.onScrollThrottled)
      this._scrollTarget = null
    }
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf)
      this._scrollRaf = null
    }
    this.unbindMirrorScroll()
  },
  methods: {
    resolveScrollTarget () {
      const custom = typeof this.scrollContainer === 'function'
        ? this.scrollContainer()
        : (this.scrollContainer ? document.querySelector(this.scrollContainer) : null)
      if (custom && typeof custom.addEventListener === 'function') return custom
      return document.querySelector('.article-dialog-content') || window
    },
    // 获取昵称列表
    getNicknameList () {
      // 未登录（如扫码进入的分享落地页）不请求，避免触发 401 弹出登录框
      if (!getToken() || !this.user || !this.user.id) return
      
      userFetch.getNickNameList().then(res => {
        if (res.code === 200 && res.data) {
          this.nicknameList = res.data
          
          // 尝试从 localStorage 恢复用户上次的选择
          const cachedNicknameId = localStorage.getItem('selectedNicknameId')
          // 「不显示学校」是隐私偏好，跟着身份一起记住（口径同 App 端），具体哪所学校不记
          const cachedHideSchool = localStorage.getItem('selectedNicknameHideSchool') === '1'
          let foundCachedNickname = false
          
          if (cachedNicknameId && this.nicknameList.length > 0) {
            // 查找缓存的昵称是否还存在
            const cachedNickname = this.nicknameList.find(nick => String(nick.id) === cachedNicknameId)
            if (cachedNickname) {
              this.selectedNickname = { ...cachedNickname, hideSchool: cachedHideSchool }
              // 老缓存没记账号，顺手补上（身份在当前账号的列表里，肯定是当前账号选的）
              this.saveNicknameChoice(cachedNickname.id, cachedHideSchool)
              foundCachedNickname = true
            } else {
              // 缓存的身份不在列表里了。网页端退出登录不清这份缓存，所以有两种可能：
              // 换了账号（缓存是上一个账号的），或者老账号多出来的匿名身份被合并删除了
              // （pp-back 的 sql/merge_duplicate_anonymous_identity.sql）。后一种不能退回第一项（实名）：
              // 用户上次选的是匿名，悄悄换成实名，下一条评论就带着真名发出去了。
              // 同一个账号的实名 id 不会变，所以「同一个账号、缓存的身份却没了」只能是匿名身份被删了，
              // 改选剩下的匿名身份并接上「不显示学校」。老缓存没记是哪个账号选的，分不清时也按这样处理：
              // 错选成匿名最多是没署名，错选成实名就是泄露身份
              const cachedOwner = localStorage.getItem('selectedNicknameOwner')
              const sameAccount = !cachedOwner || cachedOwner === String(this.user.id)
              const anonymous = sameAccount && this.nicknameList.find(nick => nick.nickType === 1)
              if (anonymous) {
                this.selectedNickname = { ...anonymous, hideSchool: cachedHideSchool }
                this.saveNicknameChoice(anonymous.id, cachedHideSchool)
                foundCachedNickname = true
              }
            }
          }
          
          // 如果没有缓存或换了账号，默认选择第一个（实名）
          if (!foundCachedNickname && this.nicknameList.length > 0) {
            this.selectedNickname = this.nicknameList[0]
            // 保存默认选择（默认显示学校；换了账号时别沿用上一个账号的「不显示学校」）
            this.saveNicknameChoice(this.selectedNickname.id, false)
          }
        }
      }).catch(err => {
        console.error('获取昵称列表失败', err)
      })
    },

    // 选择昵称。option 来自 identityOptions，带学校的选项还带着这一项对应的学校
    selectNickname (option) {
      // 「不显示学校」那一项不带学校，也不清空 commentSchoolId，切回带学校的选项还是原来那一所
      if (option.schoolId && !option.hideSchool) {
        this.commentSchoolId = String(option.schoolId)
        this.commentSchoolName = option.schoolName || ''
      }
      this.selectedNickname = {
        id: option.id,
        name: option.name,
        avatar: option.avatar,
        nickType: option.nickType,
        hideSchool: !!option.hideSchool
      }
      this.showNicknameSelector = false

      // 身份和「不显示学校」存进 localStorage，具体哪所学校不存：学校要按当前这条帖子重新决定
      // （同校帖锁死本帖学校），沿用上次在别处选的会串到别的帖子上
      this.saveNicknameChoice(option.id, option.hideSchool)
    },

    // 记住选中的身份、是否「不显示学校」，以及是哪个账号选的。
    // 记账号是为了下次缓存的身份对不上时，分得清是换了账号还是匿名身份被删了（见 getNicknameList）
    saveNicknameChoice (id, hideSchool) {
      localStorage.setItem('selectedNicknameId', String(id))
      localStorage.setItem('selectedNicknameHideSchool', hideSchool ? '1' : '0')
      if (this.user && this.user.id) {
        localStorage.setItem('selectedNicknameOwner', String(this.user.id))
      }
    },

    // 身份下拉每一项的副标题：「实名 / 匿名」后面缀上这一项对应的学校名；
    // 「不显示学校」那一项缀「不显示学校」。Reply.vue 有同名方法，改一处记得同步改另一处
    identityTypeText (option) {
      if (!option) return '实名'
      const prefix = option.nickType === 1 ? '匿名' : '实名'
      if (option.hideSchool) return `${prefix} · 不显示学校`
      const school = option.schoolName || this.commentSchoolName || this.profileSchoolName
      return school ? `${prefix} · ${school}` : prefix
    },

    // 拉一次可选学校（教育经历）。只在用户展开身份下拉时调用。
    //
    // 为什么可以懒加载：不选学校时提交不带 authorSchoolId，服务端
    // resolveAuthorSchoolId(userId, null) 会退到「资料展示的那段经历」，
    // 正好就是产品要的默认值「最近学校」；同校帖更是服务端强制用本帖学校。
    ensureCommentSchoolOptions () {
      if (this.commentSchoolOptionsLoaded || this.commentSchoolOptionsLoading) return
      // 未登录（如扫码进入的分享落地页）不请求，避免触发 401 弹出登录框
      if (!getToken() || !this.user || !this.user.id) return
      this.commentSchoolOptionsLoading = true
      getEducationList().then(res => {
        if (res.code !== 200 || !Array.isArray(res.data)) return
        // 同一所学校可能有多段经历（本科+研究生），评论只关心学校本身，按 schoolId 去重
        const seen = new Set()
        const options = []
        res.data.forEach(item => {
          if (!item || !item.schoolId) return
          const key = String(item.schoolId)
          if (seen.has(key)) return
          seen.add(key)
          options.push(item)
        })
        this.commentSchoolOptions = options
        this.commentSchoolOptionsLoaded = true
        this.applyDefaultCommentSchool()
      }).catch(err => {
        console.warn('加载可选学校失败', err)
      }).finally(() => {
        this.commentSchoolOptionsLoading = false
      })
    },

    // 学校列表到位后定下默认选中的那一所，口径和服务端保持一致：
    //   同校帖 → 本帖所属学校（commentSchoolChoices 已经只剩这一所）
    //   普通帖 → 资料展示的那段经历，对齐服务端 resolveProfileEducation
    //            （手动置顶优先，否则取排序最近的一段）
    applyDefaultCommentSchool () {
      const choices = this.commentSchoolChoices
      if (!choices.length) return
      // 已经选过且仍然合法就不覆盖用户的选择
      const kept = this.commentSchoolId
        ? choices.find(item => String(item.schoolId) === String(this.commentSchoolId))
        : null
      const selected = kept
        || choices.find(item => item.profileDisplay)
        || choices.find(item => item.recent)
        || choices[0]
      if (!selected) return
      this.commentSchoolId = String(selected.schoolId)
      this.commentSchoolName = selected.schoolName || ''
    },

    /**
     * 把 /parentList 返回的一条父评论整理成列表用的形状。
     *
     * getParentComments 和 loadMoreParentComments 原来各写了一份一模一样的字面量，
     * 加字段时漏改一处的表现是「翻到第 2 页之后某个功能就失效了」，很难联想到原因，
     * 所以收成一个方法。
     *
     * children 用服务端预取的 childRecords 兜底：折叠态直接把前两条回复渲染出来，
     * 用户不用先点一次「展开N条回复」。childrenLoaded 仍然是 false ——
     * 它的语义是「楼内回复是否已经完整分页加载过」，预取的两条不算，
     * 模板正是靠它决定显示「展开X条回复」还是「加载更多回复」。
     */
    buildParentComment (item) {
      return {
        ...item,
        // 确保 childCount 为数字类型（JSONBig 可能把数字解析成字符串）。
        // 这个值现在由服务端实时 COUNT 校正过（见后端 attachChildPreview），
        // 比 b_comment.child_count 那个异步累加值准
        childCount: Number(item.childCount) || 0,
        // 强制覆盖这些属性，确保初始状态正确
        children: item.childRecords || [],
        childrenLoaded: false,
        childrenLoading: false,
        childrenHasMore: true,
        childrenPageNo: 1,
        // 楼内往前翻的游标，只有锚点楼才会是非默认值（见 applyAnchorFloor）
        childrenFirstPageNo: 1,
        childrenHasPrevious: false,
        childrenPreviousCount: 0
      }
    },

    /**
     * 折叠态只露前 CHILD_PREVIEW_SIZE 条回复，展开后给全部已加载的。
     *
     * 用方法而不是 computed：回复是在父评论的 v-for 里渲染的，
     * computed 没法按行缓存（同 mentionSegments）。
     */
    visibleChildren (item) {
      const children = (item && item.children) || []
      if (item.childrenLoaded) return children
      return children.length > CHILD_PREVIEW_SIZE
        ? children.slice(0, CHILD_PREVIEW_SIZE)
        : children
    },

    /**
     * 折叠态被藏起来的回复条数（「展开X条回复」里的 X）。
     *
     * 用「总数 - 预览条数」：预览已经露出两条了，按钮上再写总数会让人
     * 以为点开还有那么多。childCount 缺失或偏小时退化成不显示按钮，
     * 而预览的两条仍然在，不会出现「有回复却完全看不到」。
     */
    hiddenChildCount (item) {
      if (!item) return 0
      const total = Number(item.childCount) || 0
      const previewCount = Math.min(((item.children) || []).length, CHILD_PREVIEW_SIZE)
      return Math.max(0, total - previewCount)
    },

    /**
     * 取一条评论的配图原图地址列表。
     *
     * 接口正常给的是切好的 imageList；再兜一层逗号串（images）是防某个接口
     * 只下发了原始串。两条都拿不到就是没有图。
     */
    commentImages (comment) {
      if (!comment) return []
      if (Array.isArray(comment.imageList) && comment.imageList.length) {
        return comment.imageList.filter(url => !!url)
      }
      if (typeof comment.images === 'string' && comment.images) {
        return comment.images.split(',').map(url => url.trim()).filter(url => !!url)
      }
      return []
    },

    // 获取父评论列表（整表替换，所以页码必须回到第一页）
    getParentComments () {
      if (this.loading) return
      this.loading = true
      this.pageData.pageNo = 1
      
      userFetch.fetchParentComments({ 
        ...this.pageData, 
        articleId: this.articleInfo?.id 
      }).then(res => {
        const records = res.data.records.map(item => this.buildParentComment(item))

        // 锚点楼要留在最前面，并且不能被这一页里的同一条评论顶掉：
        // 它身上带着「已展开的回复 + 楼内分页游标」，而 records 里那条同 id 的评论
        // 是干净的（children 为空），直接覆盖等于把刚定位到的回复又收起来。
        const anchor = this.anchorRootId
          ? this.commentList.find(item => String(item.id) === this.anchorRootId)
          : null
        this.commentList = anchor
          ? [anchor, ...records.filter(item => String(item.id) !== this.anchorRootId)]
          : records

        this.pages = res.data.pages
        this.hasMore = this.pageData.pageNo < res.data.pages
        this.loading = false
        
        // 列表就位后再试一次定位。
        // 两个入口（commentId watcher / 这里）谁先到谁执行，locateAndHighlight
        // 内部按 commentId 去重，不会重复请求
        this.locateAndHighlight()
      }).catch(() => {
        this.loading = false
      })
    },

    // 定位并高亮目标评论：取回「锚点楼」→ 置顶展开 → 滚过去。
    //
    // 同一个 commentId 只跑一次。触发点有两个（commentId prop 变化、父评论列表加载完），
    // 它们的先后顺序取决于弹窗是新开还是复用，所以两边都调、这里去重。
    locateAndHighlight () {
      const target = String(this.commentId || '')
      const articleId = this.articleInfo?.id
      // 帖子还没到就先不管，等 getParentComments 那一路再来
      if (!target || !articleId) return
      if (this._locatingFor === target) return
      this._locatingFor = target

      userFetch.locateComment({ articleId, commentId: target }).then(res => {
        const data = res?.data
        if (!data || !data.found) {
          // 定位失败不影响浏览：评论区照常展示，只是不定位，并说明原因。
          // 文案由服务端给（评论已删除 / 审核中 / 未通过审核…），App 端和网页端共用一份
          if (data?.message) this.$toast.info(data.message)
          // 失败也要放开重试标记。
          //
          // 弹窗是全局单例，openDialog 先写 commentId 再写 articleId：如果在弹窗还开着的
          // 状态下再点开另一条评论，commentId 的 watcher 会立刻带着「新 commentId + 上一篇的
          // articleId」来定位，服务端如实返回 NOT_IN_ARTICLE。标记不放开的话，
          // 紧跟其后 getParentComments 那一路的正常重试会被这个标记吞掉，
          // 这次弹窗就再也不会定位了。
          this._locatingFor = ''
          return
        }
        this.applyAnchorFloor(data)
      }).catch(() => {
        // 定位接口挂了不弹提示：用户是来看帖子的，评论区已经正常显示，
        // 为一个辅助功能报错只会造成困扰。允许下次重试
        this._locatingFor = ''
      })
    },

    // 把服务端算好的「锚点楼」置顶插进评论列表。
    applyAnchorFloor (data) {
      const parent = data.parentComment
      if (!parent || !parent.id) {
        // 服务端说定位成功（found=true）却没给父评论，属于接口自身的问题。
        // 必须留日志：静默 return 会让整个定位功能「什么都不做也不报错」，无迹可循。
        console.error('定位接口返回 found=true 但缺少 parentComment，无法置顶锚点楼:', data)
        // 退回最朴素的做法：目标要是刚好已经渲染在页面上（第一页的父评论），
        // 直接滚过去也能用。网页端没有别的兜底路径了，能救一种算一种
        this.$nextTick(() => {
          this.scrollToComment(String(this.commentId))
        })
        return
      }

      const rootId = String(parent.id)

      // 目标是回复（data.child）时，data.childRecords 是「目标所在那一页回复」，
      // 要整页算作已加载并展示出来，否则用户点通知跳进来看不到被回复的那一条。
      //
      // 目标是父评论时 data.childRecords 是空的，楼内回复走 parent.childRecords
      // 那份预取（和 /parentList 完全一致的形状），保持折叠态 ——
      // 锚点楼因此和它下面的楼层长得一样，而不是唯一一个默认全展开的异类。
      const isChildTarget = !!data.child
      const children = isChildTarget
        ? (data.childRecords || [])
        : (parent.childRecords || [])
      const childPageNo = Number(data.childPageNo) || 0
      // pageSize 以服务端为准并存进楼里：childPageNo 是按服务端的 LOCATE_CHILD_PAGE_SIZE
      // 切出来的，后续往前往后翻必须沿用同一个值。只靠两边各写一个 10 的约定，
      // 改了后端就会静默错位（重复出现看过的回复 + 漏掉一部分）
      const childPageSize = Number(data.childPageSize) || CHILD_COMMENT_PAGE_SIZE

      const floor = {
        ...parent,
        childCount: Number(parent.childCount) || 0,
        children,
        // 只有「目标是回复」时才算已加载：那一页里有要高亮的那条，
        // 模板据此隐藏「展开X条回复」、显示「加载更多回复」。
        // 目标是父评论时 children 只是预取的两条，不能算已完整加载 ——
        // 算了的话「展开X条回复」不显示，剩下的回复就再也点不出来了
        childrenLoaded: isChildTarget && children.length > 0,
        childrenLoading: false,
        childrenHasMore: !!data.childHasMore,
        // 把服务端算出的楼内页码写进游标，往后翻用 +1、往前翻用 -1。
        // 两个游标都必须来自服务端那次查询，否则接着翻会重复或跳页
        childrenPageNo: childPageNo,
        childrenPageSize: childPageSize,
        childrenFirstPageNo: childPageNo,
        childrenHasPrevious: !!data.childHasPrevious,
        // 目标之前还有多少条回复没加载，用来给「查看更早的回复」写个具体数字。
        // 按页码估算，不是精确条数 —— 精确值要再查一次库，为一句提示不值得
        childrenPreviousCount: childPageNo > 1 ? (childPageNo - 1) * childPageSize : 0
      }

      this.anchorRootId = rootId
      this.commentList = [
        floor,
        ...this.commentList.filter(item => String(item.id) !== rootId)
      ]

      // 目标节点此刻已经在数据里，等这一帧渲染完就能拿到 ref
      this.$nextTick(() => {
        this.scrollToComment(String(this.commentId))
      })
    },

    // 加载子评论
    loadChildComments (parentComment) {
      if (parentComment.childrenLoading) return
      
      this.$set(parentComment, 'childrenLoading', true)
      
      userFetch.fetchChildComments({
        pageNo: 1,
        pageSize: CHILD_COMMENT_PAGE_SIZE,
        parentId: parentComment.id
      }).then(res => {
        this.$set(parentComment, 'children', res.data.records)
        this.$set(parentComment, 'childrenLoaded', true)
        this.$set(parentComment, 'childrenLoading', false)
        this.$set(parentComment, 'childrenHasMore', res.data.current < res.data.pages)
        this.$set(parentComment, 'childrenPageNo', 1)
        this.$set(parentComment, 'childrenPageSize', CHILD_COMMENT_PAGE_SIZE)
        // 这是「从第 1 页重新展开」，前面不可能还有回复。
        // 归零同时负责清掉锚点楼留下的残值：定位过的那一楼被手动收起再展开时，
        // 不重置的话会看到一个指向不存在页码的「查看更早的回复」
        this.$set(parentComment, 'childrenFirstPageNo', 1)
        this.$set(parentComment, 'childrenHasPrevious', false)
        this.$set(parentComment, 'childrenPreviousCount', 0)
      }).catch(() => {
        this.$set(parentComment, 'childrenLoading', false)
      })
    },

    // 加载更多子评论
    loadMoreChildComments (parentComment) {
      if (parentComment.childrenLoading || !parentComment.childrenHasMore) return
      
      this.$set(parentComment, 'childrenLoading', true)
      const nextPage = parentComment.childrenPageNo + 1
      
      userFetch.fetchChildComments({
        pageNo: nextPage,
        // 沿用这一楼既有的 pageSize（锚点楼的来自服务端），换个值翻会让偏移错位
        pageSize: parentComment.childrenPageSize || CHILD_COMMENT_PAGE_SIZE,
        parentId: parentComment.id
      }).then(res => {
        // 去重再追加：锚点楼的回复是服务端直接给的那一页，
        // 用户接着往后翻时，边界上可能与已有的重合
        const existingIds = new Set((parentComment.children || []).map(c => String(c.id)))
        const incoming = (res.data.records || []).filter(c => !existingIds.has(String(c.id)))
        parentComment.children.push(...incoming)
        this.$set(parentComment, 'childrenPageNo', nextPage)
        this.$set(parentComment, 'childrenHasMore', res.data.current < res.data.pages)
        this.$set(parentComment, 'childrenLoading', false)
      }).catch(() => {
        this.$set(parentComment, 'childrenLoading', false)
      })
    },

    // 楼内往前翻：加载「更早的回复」。
    //
    // 只有从消息通知定位过来、且目标回复排在楼内很后面时才会出现这个入口：
    // 服务端直接给的是目标所在那一页（比如第 5 页），它前面 4 页还没加载。
    // 手动点「展开N条回复」永远从第 1 页开始，走不到这里。
    loadPrevChildComments (parentComment) {
      if (parentComment.childrenLoading || !parentComment.childrenHasPrevious) return

      const pageNo = (parentComment.childrenFirstPageNo || 1) - 1
      if (pageNo < 1) {
        // 游标已经到第 1 页，按钮不该还在，纠正状态而不是发一次必然为空的请求
        this.$set(parentComment, 'childrenHasPrevious', false)
        this.$set(parentComment, 'childrenPreviousCount', 0)
        return
      }

      this.$set(parentComment, 'childrenLoading', true)

      // 记下当前第一条回复的位置：往列表顶部插入内容会把它连同下面的内容整体推下去，
      // 用户正在读的位置会被顶出视口，加载完要挪回来
      const children = parentComment.children || []
      const anchorChildId = children.length ? String(children[0].id) : ''
      const beforeTop = this.measureCommentTop(anchorChildId)

      // 沿用这一楼既有的 pageSize，理由同 loadMoreChildComments
      const pageSize = parentComment.childrenPageSize || CHILD_COMMENT_PAGE_SIZE

      userFetch.fetchChildComments({
        pageNo,
        pageSize,
        parentId: parentComment.id
      }).then(res => {
        const existing = parentComment.children || []
        const existingIds = new Set(existing.map(c => String(c.id)))
        // 往前插：这一页在时间上排在已加载那批之前（子评论按 create_time 正序排）
        const merged = [
          ...(res.data.records || []).filter(c => !existingIds.has(String(c.id))),
          ...existing
        ]
        this.$set(parentComment, 'children', merged)
        this.$set(parentComment, 'childrenFirstPageNo', pageNo)
        this.$set(parentComment, 'childrenHasPrevious', pageNo > 1)
        this.$set(parentComment, 'childrenPreviousCount', (pageNo - 1) * pageSize)
        this.$set(parentComment, 'childrenLoading', false)

        this.restoreCommentTop(anchorChildId, beforeTop)
      }).catch(() => {
        this.$set(parentComment, 'childrenLoading', false)
      })
    },

    // 量一下某条评论此刻在视口里的位置（配合 restoreCommentTop 使用）
    measureCommentTop (commentId) {
      if (!commentId) return null
      const nodes = this.$refs['commentContent' + commentId]
      const node = nodes && nodes[0]
      return node ? node.getBoundingClientRect().top : null
    },

    // 在列表顶部插入内容之后，把滚动位置补回去，让参照那条评论停在原处。
    //
    // 不做这件事的话，「查看更早的回复」一次插入 10 条，页面内容整体下移约一屏，
    // 用户刚才在读的那条直接被顶走 —— 观感上像是页面自己乱跳了一下。
    restoreCommentTop (commentId, beforeTop) {
      if (!commentId || beforeTop === null || beforeTop === undefined) return
      this.$nextTick(() => {
        const nodes = this.$refs['commentContent' + commentId]
        const node = nodes && nodes[0]
        if (!node) return
        const delta = node.getBoundingClientRect().top - beforeTop
        // 1px 以内不折腾，避免因为小数取整反复微调
        if (Math.abs(delta) < 1) return
        // 滚动容器可能是弹窗内部，也可能是整个窗口（独立详情页），
        // 复用 mounted 里已经选好的那个
        const target = this._scrollTarget
        if (!target) return
        if (target === window) {
          window.scrollBy(0, delta)
        } else {
          target.scrollTop += delta
        }
      })
    },

    // 用 rAF 节流：一帧最多测一次布局，避免滚动时反复 reflow
    onScrollThrottled () {
      if (this._scrollRaf) return
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null
        this.handleScroll()
      })
    },

    // 触底加载更多（弹窗容器与窗口滚动统一走这里）
    handleScroll () {
      if (this.loading || !this.hasMore) return

      const target = this._scrollTarget
      if (!target) return

      let scrollTop
      let scrollHeight
      let clientHeight
      if (target === window) {
        const doc = document.documentElement
        scrollTop = doc.scrollTop || document.body.scrollTop
        scrollHeight = doc.scrollHeight
        clientHeight = window.innerHeight
      } else {
        scrollTop = target.scrollTop
        scrollHeight = target.scrollHeight
        clientHeight = target.clientHeight
      }

      if (scrollHeight - scrollTop - clientHeight < 200) {
        this.loadMoreParentComments()
      }
    },

    // 加载更多父评论
    loadMoreParentComments () {
      if (this.loading || !this.hasMore) return
      
      this.pageData.pageNo += 1
      this.loading = true
      
      userFetch.fetchParentComments({ 
        ...this.pageData, 
        articleId: this.articleInfo?.id 
      }).then(res => {
        // 去重：锚点楼被置顶插到了列表最前面，它在自己本来的页码上还会再回来一次。
        // 不去重的话同一条评论会在列表里出现两遍，而且第二次那条是没展开的干净副本
        const existingIds = new Set(this.commentList.map(item => String(item.id)))
        const newComments = res.data.records
          .filter(item => !existingIds.has(String(item.id)))
          .map(item => this.buildParentComment(item))
        this.commentList.push(...newComments)
        this.pages = res.data.pages
        this.hasMore = this.pageData.pageNo < res.data.pages
        this.loading = false
      }).catch(() => {
        this.loading = false
        this.pageData.pageNo -= 1
      })
    },

    // 滚动到指定评论位置
    scrollToComment (commentId) {
      const refName = 'commentContent' + commentId
      const commentElement = this.$refs[refName]
      
      if (commentElement && commentElement[0]) {
        commentElement[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        commentElement[0].style.transition = 'all 0.5s ease'
        commentElement[0].style.background = 'linear-gradient(135deg, rgba(135, 206, 250, 0.2) 0%, rgba(173, 216, 230, 0.15) 100%)'
        commentElement[0].style.boxShadow = '0 0 0 3px rgba(135, 206, 250, 0.3)'
        commentElement[0].style.borderRadius = '8px'
        
        setTimeout(() => {
          commentElement[0].style.background = ''
          commentElement[0].style.boxShadow = ''
        }, 3000)
      }
    },

    // 取主评论框的原生 textarea
    getMainTextarea () {
      const textarea = this.$refs.editableDiv
      if (!textarea) return null
      return textarea.$el ? textarea.$el.querySelector('textarea') : textarea
    },

    /**
     * 供外部（移动端底部操作条）调用：聚焦主评论框
     * 注意 focus() 必须尽量同步执行，iOS Safari 只在用户手势的同一个任务里才会拉起软键盘。
     */
    focusMainInput () {
      // 之前点过「回复」会把主评论框隐藏，这里先恢复
      if (!this.showMainCommentBox) {
        this.showMainCommentBox = true
        this.$nextTick(() => {
          const el = this.getMainTextarea()
          if (el) el.focus()
          this.scrollMainInputIntoView()
        })
        return
      }

      const el = this.getMainTextarea()
      if (el) el.focus()
      this.scrollMainInputIntoView()
    },

    scrollMainInputIntoView () {
      const box = this.$el && this.$el.querySelector('.comment-item')
      box && box.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    selectCommentSticker(item) {
      if (!item || !item.url) return
      this.commentSticker = { url: item.url, name: item.name || '' }
      this.resetCommentImages()
      if (!item.added) {
        // 走 collectStickerAfterSend 而不是直接 addStickerByUrl：收藏满 100 之后
        // 后者每次都会白打一次必然失败的请求，详见 mixins/stickerMixin.js
        this.collectStickerAfterSend(item)
      }
    },
    async uploadCommentSticker(file) {
      try {
        const url = await uploadImageToCos(file, { directory: 'sticker' })
        await this.addStickerByUrl(url)
        this.$toast.success('已添加')
      } catch (e) {
        this.$toast.error((e && e.message) || '添加失败')
      }
    },
    async pinCommentSticker(item) {
      try {
        await this.pinStickerById(item.id)
      } catch (e) {
        this.$toast.error((e && e.message) || '操作失败')
      }
    },
    async removeCommentSticker(item) {
      try {
        await this.removeStickerById(item.id, item.url)
        this.$toast.success('已删除')
      } catch (e) {
        this.$toast.error((e && e.message) || '删除失败')
      }
    },
    async addRecommendSticker(item) {
      try {
        await this.addStickerByUrl(item.url, item.name)
        this.$toast.success('已添加')
      } catch (e) {
        this.$toast.error((e && e.message) || '添加失败')
      }
    },
    handleChooseEmoji (emoji) {
      const textareaEl = this.getMainTextarea()
      if (!textareaEl) return
      
      // 获取当前光标位置
      const startPos = textareaEl.selectionStart
      const endPos = textareaEl.selectionEnd
      const currentValue = this.commentContent || ''
      
      // 在光标位置插入表情
      const newValue = currentValue.substring(0, startPos) + emoji + currentValue.substring(endPos)
      this.commentContent = newValue
      
      // 设置新的光标位置（表情后面）
      this.$nextTick(() => {
        const newPos = startPos + emoji.length
        textareaEl.setSelectionRange(newPos, newPos)
        textareaEl.focus()
      })
    },

    replyComment (item, parentId, isChildren) {
      // 注意：store 里 userInfo 的初始值是 {}，用 == null 判断永远为假，
      // 未登录用户点「回复」会直接打开回复框、发出去才被 401 拦下来。
      if (!this.user) {
        this.$store.commit("systemStore/setLoginFlag", true)
        return
      }
      
      // 检查是否点击的是同一个回复按钮
      const isSameComment = this.lastCommentId === item.id
      const refName = isChildren ? 'replys' + item.id : 'reply' + item.id
      const currentShowBox = this.$refs[refName] && this.$refs[refName][0] && this.$refs[refName][0].showBox
      
      // 关闭之前打开的回复框
      if (this.$refs['replys' + this.lastCommentId] != null) {
        this.$refs['replys' + this.lastCommentId][0].showBox = false
      }
      if (this.$refs['reply' + this.lastCommentId] != null) {
        this.$refs['reply' + this.lastCommentId][0].showBox = false
      }

      // 如果点击的是同一个回复按钮且回复框已经打开，则只关闭不再打开
      if (isSameComment && currentShowBox) {
        this.lastCommentId = null
        // 重新显示主评论输入框
        this.showMainCommentBox = true
        return
      }

      // 隐藏主评论输入框
      this.showMainCommentBox = false

      // 打开新的回复框
      if (isChildren) {
        this.$refs['replys' + item.id][0].showBox = true
        this.$refs['replys' + item.id][0].commentContent = ""
        this.$refs['replys' + item.id][0].nickname = item.nickname
        this.$refs['replys' + item.id][0].replyUserId = item.userId
        this.$refs['replys' + item.id][0].parentId = parentId
        this.$refs['replys' + item.id][0].index = item.id
        this.$refs['replys' + item.id][0].level = item.level
      } else {
        this.$refs['reply' + item.id][0].showBox = true
        this.$refs['reply' + item.id][0].commentContent = ""
        this.$refs['reply' + item.id][0].nickname = item.nickname
        this.$refs['reply' + item.id][0].replyUserId = item.userId
        this.$refs['reply' + item.id][0].parentId = parentId
        this.$refs['reply' + item.id][0].index = item.id
        this.$refs['reply' + item.id][0].level = item.level
      }
      this.lastCommentId = item.id
    },

    reloadReply (parentId) {
      // 找到对应的父评论，只刷新该父评论的子评论列表
      // 使用字符串比较，避免类型不匹配问题
      const parentIdStr = String(parentId)
      const parentComment = this.commentList.find(item => String(item.id) === parentIdStr)
      
      if (parentComment) {
        // 增加子评论数量
        this.$set(parentComment, 'childCount', (parentComment.childCount || 0) + 1)
        
        // 通知父组件评论数量增加
        this.$emit('comment-added')
        
        // 如果子评论已经展开，重新加载子评论列表
        if (parentComment.childrenLoaded) {
          this.$set(parentComment, 'childrenLoading', true)
          userFetch.fetchChildComments({
            pageNo: 1,
            pageSize: CHILD_COMMENT_PAGE_SIZE,
            parentId: parentComment.id
          }).then(res => {
            this.$set(parentComment, 'children', res.data.records)
            this.$set(parentComment, 'childrenHasMore', res.data.current < res.data.pages)
            this.$set(parentComment, 'childrenPageNo', 1)
            this.$set(parentComment, 'childrenLoading', false)
            // 往前翻的游标必须一起归零：这里重拉的是第 1 页，前面不可能还有回复。
            //
            // 这一步不能省。网页端没有「收起」入口，childrenLoaded 一旦为 true 就不会
            // 再回退，所以 loadChildComments 里那套归零对锚点楼永远走不到 ——
            // 责任全在这里。漏掉的话：从通知定位到楼内第 5 页的回复，在这一楼里回复一句，
            // children 换成了第 1 页的 10 条，顶部却还挂着「查看更早的 40 条回复」，
            // 点下去会把第 31~40 条插到第 1~10 条前面，楼内时间顺序直接倒挂。
            this.$set(parentComment, 'childrenFirstPageNo', 1)
            this.$set(parentComment, 'childrenHasPrevious', false)
            this.$set(parentComment, 'childrenPreviousCount', 0)
          }).catch(() => {
            this.$set(parentComment, 'childrenLoading', false)
          })
        } else {
          // 如果子评论未展开，自动展开并加载
          this.loadChildComments(parentComment)
        }
      }
    },

    // 处理关闭回复框事件
    handleCloseReply () {
      // 重新显示主评论输入框
      this.showMainCommentBox = true
    },

    // 把正文切成「普通文本 / @提及」片段，供模板渲染高亮链接。
    // 用方法而不是 computed：评论是 v-for 出来的，computed 没法按行缓存
    mentionSegments (comment) {
      if (!comment) return []
      return parseMentionSegments(comment.content, comment.atUsers)
    },

    // 取输入框内部真正的 textarea 元素。
    // el-input 是个包装组件，光标位置只能从它内部的原生元素上取（同 handleChooseEmoji）
    getTextareaEl () {
      const wrapper = this.$refs.editableDiv
      return wrapper && wrapper.$el ? wrapper.$el.querySelector('textarea') : null
    },

    // 把 @ 高亮镜像滚到和 textarea 相同的位置。
    // 输入框只有 4 行高，正文长了就会内部滚动，而镜像是另一个元素，
    // 不跟着滚就会和真实文字错开成双影。
    // 网页端能直接读 textarea.scrollTop —— App 端拿不到这个值，
    // 那边只能在内容溢出时干脆把高亮关掉（见 uniapp 的 showInputHighlight）
    syncMirrorScroll () {
      const textareaEl = this.getTextareaEl()
      const mirror = this.$refs.inputMirror
      if (textareaEl && mirror) {
        mirror.scrollTop = textareaEl.scrollTop
      }
      // 选人面板的锚点是相对输入框定位的，内容滚动之后 "@" 的可视位置变了，
      // 面板得跟着走，否则会停在原地和 "@" 脱开
      if (this.atTriggerIndex >= 0) {
        this.updateMentionAnchor()
      }
    },

    // 给 textarea 的滚动挂上镜像同步。
    // 只能拿到内部原生元素自己 addEventListener：scroll 事件不冒泡，
    // @scroll.native 挂在 el-input 根节点上是收不到的
    bindMirrorScroll () {
      const textareaEl = this.getTextareaEl()
      if (!textareaEl || this._mirrorScrollEl === textareaEl) return
      this.unbindMirrorScroll()
      this._mirrorScrollEl = textareaEl
      // Vue 2 会把 methods 逐实例绑定好，这里的函数引用是稳定的，能正常摘掉
      textareaEl.addEventListener('scroll', this.syncMirrorScroll, { passive: true })
    },

    unbindMirrorScroll () {
      if (this._mirrorScrollEl) {
        this._mirrorScrollEl.removeEventListener('scroll', this.syncMirrorScroll)
        this._mirrorScrollEl = null
      }
    },

    // 输入时做两件事：
    //   1) 用正文校准已选的 @ 列表（和 App 端一致）。只在提交时校准是不够的：
    //      勾选态、"N/10" 计数、以及能不能再选人的上限判断读的都是这个列表，
    //      用户把 10 个提及全删了却一个人也加不进去；
    //   2) 判断是不是刚打了 "@" 想找人，是则把选人面板顶出来，并把已输入的关键词带过去。
    // 光标位置从原生 textarea 取，所以在句子中间打 @ 也能正确识别作用域
    onContentInput () {
      if (this.selectedAtUsers.length) {
        const kept = syncAtUsers(this.commentContent, this.selectedAtUsers)
        if (kept.length !== this.selectedAtUsers.length) {
          this.selectedAtUsers = kept
        }
      }

      const textareaEl = this.getTextareaEl()
      const query = detectMentionQuery(this.commentContent, textareaEl ? textareaEl.selectionStart : undefined)
      if (!query) {
        // 从"正在搜人"退出（选完人 / 打了空格 / 把 @ 删了）：清掉替换锚点并收起面板。
        // 面板里已经没有搜索框了，搜什么完全取决于正文里那段 "@xxx"，
        // 这段没了就没有任何东西可搜，留着一个列表在那儿只会挡住评论
        this.atTriggerIndex = -1
        this.closeMentionPicker()
        return
      }
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) return

      this.atTriggerIndex = query.index
      if (this.$refs.mentionPicker) {
        this.$refs.mentionPicker.open(query.keyword)
      }
      // 探针要等这一帧渲染出来才能量。每次输入都重算一遍："@" 之前的正文变了、
      // 或者折行位置变了，它的像素位置就会变
      this.$nextTick(this.updateMentionAnchor)
    },

    // 把选人面板的锚点挪到正文里那个 "@" 的像素位置上，面板就紧跟在 "@" 后面展开。
    //
    // textarea 没有任何 API 能问出"第 n 个字符画在哪"，只能靠 .comment-caret-probe
    // 这个盒模型一致的探针把前半段文字排一遍再读坐标，原理见模板里的注释。
    updateMentionAnchor () {
      const mark = this.$refs.caretProbeMark
      const textareaEl = this.getTextareaEl()
      if (!mark || !textareaEl) return

      // 标记是 inline-block、高度写成了 1em，offsetHeight 就是一行的高度
      const lineHeight = mark.offsetHeight || 20
      // 锚点落在 "@" 所在那一行的下沿。
      // 减掉 scrollTop：探针跟着内容排版，而锚点是相对输入框定位的 ——
      // 输入框内部滚动之后两者正好差一个 scrollTop
      const left = mark.offsetLeft
      const top = mark.offsetTop + lineHeight - textareaEl.scrollTop

      this.mentionAnchorStyle = { left: `${left}px`, top: `${top}px` }
      // 锚点是用内联 style 挪的，popper.js 感知不到，得等 style 落到 DOM 上之后
      // 显式让它重算一次位置
      this.$nextTick(() => {
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.reposition()
      })
    },

    closeMentionPicker () {
      if (this.$refs.mentionPicker) {
        this.$refs.mentionPicker.close()
      }
    },

    // 点工具栏的 @ 按钮：往光标处插一个 "@"，剩下的交给 onContentInput 统一处理。
    //
    // 这样面板的开合和搜索词只有一个事实来源 —— 正文里有没有一段「正在输入的 @」。
    // 不存在"按钮打开的面板"和"打字触发的面板"两套状态，选人时也只走
    // insertMention 这一条替换路径（App 端同款做法）。
    onMentionButtonClick () {
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) {
        this.$toast.info(`最多只能@${MAX_AT_USER_COUNT}个人`)
        return
      }

      const textareaEl = this.getTextareaEl()
      const content = this.commentContent || ''
      const start = textareaEl ? textareaEl.selectionStart : content.length
      const end = textareaEl ? textareaEl.selectionEnd : content.length

      // 正文里已经有一段正在输入的 @ 了：别再插第二个（会变成 "@ @"，用户得自己回去删），
      // 只把面板顶出来
      const pending = detectMentionQuery(content, start)
      if (pending) {
        this.atTriggerIndex = pending.index
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.open(pending.keyword)
        if (textareaEl) textareaEl.focus()
        this.$nextTick(this.updateMentionAnchor)
        return
      }

      if (content.length + 1 > COMMENT_MAX_LENGTH) {
        this.$toast.info(`评论最多输入 ${COMMENT_MAX_LENGTH} 个字`)
        return
      }

      const before = content.slice(0, start)
      const after = content.slice(end)
      // "@" 前面不是空白就补一个空格，否则会粘成 "你好@张三"，
      // 读起来像句子的一部分而不是一个提及（和 insertMentionAt 同一个口径）
      const prefix = (before && !/\s$/.test(before)) ? ' ' : ''
      const cursor = before.length + prefix.length + 1

      this.commentContent = before + prefix + '@' + after
      this.atTriggerIndex = cursor - 1

      this.$nextTick(() => {
        if (textareaEl) {
          textareaEl.setSelectionRange(cursor, cursor)
          textareaEl.focus()
        }
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.open('')
        // atTriggerIndex 上面已经同步置好，探针这一帧就在了，可以量位置
        this.updateMentionAnchor()
      })
    },

    // 退格删 "@昵称"：第一次按整段选中，再按一次才真的删掉。
    //
    // 为什么要两步：原生行为是一个字一个字删，光标停在 "@张三" 后面按一下退格
    // 只删掉"三"，剩下 "@张" 是一段没有意义的文本（那个人已经不在 atUserIds 里了），
    // 用户还得再按几次才能清干净。而"按一次就整段消失"又太突然 ——
    // 一个昵称最长十几个字，凭空少掉一大截容易让人以为误删了别的内容。
    // 微信、小红书都是这个两步手感。
    //
    // 网页端的实现比 App 端干净得多：这里有真正的 keydown 和真正的选区，
    // 所以能在删除发生<b>之前</b>拦下来，用 setSelectionRange 把整段选起来，
    // 选中高亮由浏览器自己画（App 端的 textarea 没有 keydown，只能靠前后文本
    // diff 反推、再自己在高亮镜像层上画选中背景，见 pp-front-uniapp 的 handleMentionDelete）。
    // 用户再按一次退格时选区非空，我们不拦，浏览器原生删掉整段 ——
    // 随后的 input 事件里 syncAtUsers 会把这个人从待提交列表里剔掉，不用额外处理。
    onTextareaKeydown (e) {
      if (!e || e.key !== 'Backspace') return
      // 中文输入法组词过程中不要插手：此刻 textarea 的值还不含正在组的字，
      // 动选区会把输入法打断
      if (e.isComposing) return
      // 组合键交给浏览器（Ctrl/Alt/Cmd + Backspace 是删词、删到行首）
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (!this.selectedAtUsers.length) return

      const textareaEl = this.getTextareaEl()
      if (!textareaEl) return

      const start = textareaEl.selectionStart
      const end = textareaEl.selectionEnd
      if (typeof start !== 'number' || typeof end !== 'number') return
      // 已经有选区（可能正是上一次按出来的那一段）→ 不拦，让浏览器原生删掉
      if (start !== end) return
      if (start <= 0) return

      // 即将被删掉的是光标前面那个字符。findMentionTokenAt 把 "@昵称" 和紧跟它的
      // 那个空格算作一个整体，所以删到那个空格时也会命中 —— 那个空格是插入提及时
      // 自动补的、用户看不见，让他先单独删一次只会觉得"按了没反应"
      const token = findMentionTokenAt(this.commentContent, this.selectedAtUsers, start - 1)
      if (!token) return

      e.preventDefault()
      // 选到 tokenEnd（含尾随空格）：不含的话删完会留下一个孤零零的空格，
      // 连续 @ 过两个人时更会留下两个
      textareaEl.setSelectionRange(token.start, token.tokenEnd)
    },

    // MentionPicker 选中一个人：把 "@昵称 " 写进输入框并记下 id。
    //
    // 两条插入路径，取决于面板是怎么打开的：
    //   - 用户打 "@" 唤起的（atTriggerIndex >= 0）：把 "@已输入的搜索词" 整段替换掉，
    //     否则会变成 "@zh @张三"；
    //   - 点 @ 按钮打开的：插到光标处。点 popover 时焦点虽然移走了，
    //     但 textarea 的选区位置还留着，所以能插回用户原本的光标处（同 handleChooseEmoji）。
    handleSelectAtUser (atUser) {
      if (!atUser || !atUser.userId || !atUser.nickname) return
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) {
        this.$toast.info(`最多只能@${MAX_AT_USER_COUNT}个人`)
        return
      }

      const textareaEl = this.getTextareaEl()
      let result
      if (this.atTriggerIndex >= 0) {
        const value = insertMention(this.commentContent, atUser.nickname, this.atTriggerIndex)
        result = { value, cursor: -1 }
      } else {
        result = insertMentionAt(
          this.commentContent,
          atUser.nickname,
          textareaEl ? textareaEl.selectionStart : undefined,
          textareaEl ? textareaEl.selectionEnd : undefined
        )
      }

      // el-input 的 maxlength 只约束键盘输入，JS 赋值不受它限制
      if (result.value.length > COMMENT_MAX_LENGTH) {
        this.$toast.info(`评论最多输入 ${COMMENT_MAX_LENGTH} 个字`)
        return
      }

      this.commentContent = result.value
      this.selectedAtUsers = [...this.selectedAtUsers, atUser]
      this.atTriggerIndex = -1

      if (textareaEl) {
        this.$nextTick(() => {
          if (result.cursor >= 0) {
            textareaEl.setSelectionRange(result.cursor, result.cursor)
          }
          textareaEl.focus()
        })
      }
    },

    async addComment () {
      if (!this.user) {
        this.$store.commit("systemStore/setLoginFlag", true)
        return
      }
      if (!isAuditPublished(this.articleInfo)) {
        const badge = getAuditBadge(this.articleInfo)
        this.$toast.info(badge ? badge.tip : '该帖子当前不可评论')
        return
      }
      // 「只发图不发字」是合法的，判据必须和后端 insertComment 的判空口径一致：
      // 它只在「既没文字也没图」时才拒
      const hasText = !!(this.commentContent && this.commentContent.trim())
      if (!hasText && !this.hasCommentImages && !this.commentSticker) {
        this.$toast.error('评论不能为空')
        return
      }
      // 连点会重复提交同一条评论，接口没有幂等保护
      if (this.submitting) return
      this.submitting = true

      // 先把配图传到 COS，拿到可入库的地址再发评论。
      // 顺序不能反：先发评论再传图的话，传图失败就留下一条本该带图却没有图的评论，
      // 而评论已经发出去了，没法回滚
      let imageUrls = []
      try {
        imageUrls = await this.uploadCommentImagesToCos()
      } catch (err) {
        console.error('评论图片上传失败:', err)
        this.$toast.error(err?.message || '图片上传失败，请重试')
        this.submitting = false
        return
      }

      const browser = browserMatch() || { browser: '', version: '' }
      const content = this.commentContent.trim()
      const comment = {
        articleId: this.articleInfo?.id,
        content,
        browser: String(browser.browser || '').toLowerCase(),
        browserVersion: `${browser.browser || ''} ${browser.version || ''}`.trim(),
        replyUserId: this.articleInfo.userId,
        type: this.articleInfo.type,
        level: 1,
        // 添加选择的昵称信息
        nickType: this.selectedNickname ? this.selectedNickname.nickType : 0,
        userId: this.selectedNickname ? this.selectedNickname.id : this.user.id,
        // 这条评论挂在哪所学校名下（落到 b_comment.author_school_id 快照）。
        // 只有普通帖需要传：同校帖服务端会强制用本帖所属学校，忽略这个值；
        // 不传（用户没展开过身份下拉）则服务端回退到资料展示的那段经历，也就是最近学校。
        // 服务端还会校验这所学校确实属于当前账号族，传了不属于的会被忽略。
        // 选了「不显示学校」时不传学校，改传 hideAuthorSchool=1：服务端据此不写快照，
        // 读取侧也不会回退到作者当前学校（落到 b_comment.hide_author_school）
        authorSchoolId: (this.selectedNickname && this.selectedNickname.hideSchool)
          ? null : (this.commentSchoolId || null),
        hideAuthorSchool: (this.selectedNickname && this.selectedNickname.hideSchool) ? 1 : 0,
        // 被@的用户id，逗号分隔。以正文为准校准一遍，避免给"名字已经被删掉"的人发通知
        atUserIds: buildAtUserIds(syncAtUsers(content, this.selectedAtUsers)),
        // 配图地址数组。字段名与后端 Comment.imageList 一致（那是个
        // @TableField(exist = false) 的入参字段，服务端清洗后落成逗号串）
        imageList: imageUrls.length ? imageUrls : null,
        stickerUrl: this.commentSticker ? this.commentSticker.url : null
      }

      userFetch.postComment(comment).then(() => {
        // 审核状态（isPublish / reviewStatus）刻意不做分支：
        // 产品要求评论侧对审核完全无感，待审(2)和不通过(3)都走这条普通成功路径。
        // 未通过审核的评论重新拉列表时不会出现（列表接口带 is_publish = 1）
        this.commentContent = ''
        this.selectedAtUsers = []
        this.resetCommentImages()
        this.commentSticker = null

        this.pageData.pageNo = 1
        this.hasMore = true
        this.getParentComments()
        this.$toast.success('评论成功')
        this.$store.commit("commonStore/setCommentFlag", true)
        // 通知父组件评论数量增加
        this.$emit('comment-added')
      }).catch(err => {
        // 原来没有 catch：失败时会抛出未处理的 Promise 异常，
        // 输入框内容还留着但用户不知道发生了什么
        if (err?.message !== '__SILENT_AUTH__' && err?.message !== '__ACCOUNT_BANNED__') {
          this.$toast.error(err?.message || '评论失败，请稍后重试')
        }
      }).finally(() => {
        this.submitting = false
      })
    },

    splitIpAddress (address) {
      if (!address) return '未知'
      return address.split("|")[1] || address
    },

    handleLike (comment) {
      if (!this.user) {
        this.$store.commit("systemStore/setLoginFlag", true)
        return
      }
      // 评论点赞是「切换」语义，连点会让最终状态取决于响应顺序
      if (comment._likePending) return
      comment._likePending = true

      userFetch.likeComment(comment.id).then(res => {
        if (res.code === 200) {
          this.$set(comment, 'hasCommentLike', !comment.hasCommentLike)
          if (comment.hasCommentLike) {
            this.$set(comment, 'commentLikeCount', (comment.commentLikeCount || 0) + 1)
            this.$toast.success('点赞成功')
          } else {
            this.$set(comment, 'commentLikeCount', Math.max((comment.commentLikeCount || 0) - 1, 0))
            this.$toast.success('取消点赞')
          }
        }
      }).catch(err => {
        if (err?.message !== '__SILENT_AUTH__' && err?.message !== '__ACCOUNT_BANNED__') {
          this.$toast.error('操作失败')
        }
      }).finally(() => {
        comment._likePending = false
      })
    },

    // ===== 评论「更多」菜单（CommentActionMenu） =====

    /**
     * 菜单点击分发。口径与 App 端 onCommentMenuAction 一致：复制不需要登录，其余都要。
     * 未登录时直接弹登录框（和回复、点赞一样），而不是只给一句提示。
     *
     * @param {String} key 菜单项，见 CommentActionMenu
     * @param {Object} comment 被操作的评论（父评论或楼内回复）
     * @param {String|null} parentId 回复所属的父评论 id；操作的是父评论时为 null
     */
    onCommentAction (key, comment, parentId) {
      if (!comment || !comment.id) return
      if (key === 'copy') {
        this.copyCommentContent(comment)
        return
      }
      if (!this.user) {
        this.$store.commit('systemStore/setLoginFlag', true)
        return
      }
      switch (key) {
        case 'share':
          this.commentShareTarget = comment
          this.commentShareVisible = true
          break
        case 'chat':
          this.openCommentChat(comment)
          break
        case 'add-sticker':
          this.collectCommentSticker(comment)
          break
        case 'report':
          this.openCommentReport(comment)
          break
        case 'delete':
          this.confirmDeleteComment(comment, parentId)
          break
        default:
          break
      }
    },

    async copyCommentContent (comment) {
      const content = String(comment.content || '').trim()
      if (!content) {
        this.$toast.info('没有可复制的内容')
        return
      }
      const ok = await copyText(content)
      if (ok) {
        this.$toast.success('已复制')
      } else {
        this.$toast.error('复制失败，请手动复制')
      }
    },

    collectCommentSticker (comment) {
      const url = collectableStickerUrl(comment)
      if (!url) return
      // addStickerByUrl 来自 stickerMixin，成功后会顺手更新本组件的表情面板数据，
      // 不用等下次打开面板重新拉
      this.addStickerByUrl(url)
        .then(() => this.$toast.success('已添加到表情'))
        .catch(() => {
          // 收藏已满之类的业务错误、网络错误都已由请求拦截器提示过
        })
    },

    openCommentChat (comment) {
      // 菜单已经按这几个条件把「私信」藏掉了，这里再兜一次（App 端 chatWithCommentUser 同款）
      if (isAnonymousComment(comment)) {
        this.$toast.info('匿名评论不支持私信')
        return
      }
      if (isCancelledCommentUser(comment)) {
        this.$toast.info('该用户已注销')
        return
      }
      if (!canChatWithCommentUser(comment) || String(comment.userId) === String(this.user.id)) {
        this.$toast.info('不能给自己发私信')
        return
      }
      this.commentDmTarget = { userId: String(comment.userId), nickname: comment.nickname || '' }
      this.commentDmVisible = true
    },

    openCommentReport (comment) {
      this.commentReportId = String(comment.id)
      // 弹窗顶部回显「谁的哪条评论」，一屏好几条评论时用户才知道自己举报的是哪一条
      this.commentReportQuote = `${comment.nickname || '评论'}：${commentSummary(comment, 80)}`
      this.commentReportVisible = true
    },

    /**
     * 删除评论：二次确认 → 调接口 → 本地摘掉。
     *
     * 能看到「删除」的有两种人（见 CommentActionMenu）：评论作者本人，和这篇帖子的作者。
     * 服务端按同一口径校验，所以这里不再自己判断权限。
     */
    async confirmDeleteComment (comment, parentId) {
      if (this.deletingCommentId) return
      const commentId = String(comment.id)
      this.deletingCommentId = commentId
      try {
        // 删父评论会把整楼回复一起删掉（服务端级联），必须提前说清楚
        const childCount = parentId ? 0 : (Number(comment.childCount) || 0)
        try {
          await this.$confirm(
            childCount > 0
              ? `删除后该评论下的 ${childCount} 条回复也会一并删除，且不可恢复`
              : '删除后将不可恢复',
            '删除这条评论',
            {
              confirmButtonText: '删除',
              cancelButtonText: '取消',
              confirmButtonClass: 'el-button--danger',
              type: 'warning',
              lockScroll: false
            }
          )
        } catch (e) {
          return
        }
        await userFetch.deleteComment(commentId)
        this.removeCommentFromList(comment, parentId)
        this.$toast.success('删除成功')
      } catch (err) {
        // 无权删除、网络异常等失败提示已由请求拦截器统一给出
      } finally {
        this.deletingCommentId = ''
      }
    },

    /**
     * 删除成功后同步本地列表，并把评论数的变化告诉父组件（详情页 / 视频播放器上的「N 条评论」）。
     *
     * 账法与服务端一致（见 pp-back ApiCommentServiceImpl#deleteComment）：
     * 删父评论会级联删掉整楼回复，帖子评论数一次扣 1 + 楼内回复数；删回复只扣 1。
     */
    removeCommentFromList (comment, parentId) {
      const commentId = String(comment.id)
      let removedCount = 1

      if (!parentId) {
        const index = this.commentList.findIndex(item => String(item.id) === commentId)
        const removed = index !== -1 ? this.commentList[index] : comment
        removedCount = 1 + (Number(removed.childCount) || 0)
        if (index !== -1) this.commentList.splice(index, 1)
        if (this.anchorRootId === commentId) this.anchorRootId = ''
      } else {
        const parent = this.commentList.find(item => String(item.id) === String(parentId))
        if (parent) {
          const children = parent.children || []
          const childIndex = children.findIndex(child => String(child.id) === commentId)
          if (childIndex !== -1) children.splice(childIndex, 1)
          const nextCount = Math.max(0, (Number(parent.childCount) || 0) - 1)
          this.$set(parent, 'childCount', nextCount)
          // 回复删光了就把楼内状态复位，不然会留一个「加载更多回复」的空按钮
          if (nextCount === 0) {
            this.$set(parent, 'children', [])
            this.$set(parent, 'childrenHasMore', false)
            this.$set(parent, 'childrenHasPrevious', false)
          }
        }
      }

      // 被删的楼里可能正开着回复框（回复的就是这条，或者楼里的某条回复）。
      // 回复框跟着节点一起没了，主评论框却还藏着，得恢复出来
      this.$nextTick(this.restoreMainCommentBoxIfOrphaned)
      this.$emit('comment-removed', removedCount)
    },

    restoreMainCommentBoxIfOrphaned () {
      if (this.lastCommentId == null) return
      const id = this.lastCommentId
      const replyRef = this.$refs['reply' + id]
      const childReplyRef = this.$refs['replys' + id]
      if ((replyRef && replyRef[0]) || (childReplyRef && childReplyRef[0])) return
      this.lastCommentId = null
      this.showMainCommentBox = true
    },
  }
}
</script>

<style lang="scss" scoped>
// 评论输入框 textarea 与盖在它上面那层 @ 高亮镜像的共享盒模型。
//
// 两层必须逐字符对齐：字号、行高、内边距、折行规则任何一项只改一边，
// 镜像里的文字就会和真实文字错开，表现是「双影 + 光标位置不对」，
// 而且偏移量会随文字增多而累积。所以这些属性一律走 mixin，不允许只改一处。
// 折行规则显式写死（而不是依赖 textarea 的浏览器默认值），
// 否则 div 和 textarea 会各自继承到不同的值。
// 镜像层的用途和取舍见模板里 .comment-input-mirror 上的注释。
@mixin comment-input-box {
  box-sizing: border-box;
  width: 100%;
  padding: 12px 15px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  letter-spacing: normal;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;
}

// 评论区头像：原来用的是 el-avatar size="medium"（自带 36px 圆形），
// 现在换成 <smart-img>（走 CDN 缩略图 + 失败降级），尺寸圆角需要自己写。
// 放在 scoped 块顶层：<smart-img> 的根元素会带上本组件的 scope 属性，能匹配到。
.comment-avatar {
  display: block;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #f2f3f5;
}

.comment-main {
  .comment-item {
    border: none;
    border-radius: 8px;
    padding: 0;
    margin: 10px 0 10px;
    background-color: #f7f8fa;
    position: relative;

    .box {
      display: flex;
      padding: 10px;

      .avatar-wrapper {
        position: relative;

        .avatar {
          line-height: normal;
          position: relative;
          vertical-align: middle;
          cursor: pointer;

          img {
            width: 40px;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
            transition: all 0.3s;
          }

          &:hover img {
            transform: scale(1.05);
          }

          .avatar-badge {
            position: absolute;
            bottom: -2px;
            right: -2px;
            width: 16px;
            height: 16px;
            background: var(--theme-color);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #f7f8fa;

            i {
              font-size: 10px;
              color: #fff;
            }
          }
        }
      }

      .ml-3 {
        margin-left: 12px !important;
        width: 100%;
        position: relative;

        .comment-input {
          position: relative;
          padding-right: 15px;

          /* 镜像层要相对输入框定位，所以给它俩套一个定位父级。
             不能直接用 .comment-input：它有 padding-right: 15px，
             镜像铺满它就会比 textarea 宽出 15px，折行位置全错。

             用 flex 而不是默认的块级流：.el-textarea 是 inline-block，
             走行内流时这个容器的高度要连带行盒的 strut 一起算，
             镜像 top: 0 就未必正好落在 textarea 的上边缘。
             flex 布局里没有行盒，容器高度就是 textarea 的高度，对齐是确定的 */
          .comment-textarea-wrap {
            position: relative;
            display: flex;
            flex-direction: column;
          }

          /* 光标探针：和 textarea 逐项同款的盒模型，只用来量 "@" 的像素位置。
             visibility: hidden 而不是 display: none —— 后者不产生布局，
             offsetLeft / offsetTop 会全是 0。
             z-index: -1 压在最底下，配合 pointer-events: none，
             保证它既看不见、也挡不住任何交互 */
          .comment-caret-probe {
            @include comment-input-box;

            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            visibility: hidden;
            pointer-events: none;
            z-index: -1;
          }

          /* 位置标记。给一个 1em 的高度：空的行内元素在部分浏览器里量不到高度，
             而这里正好要用它的高度当行高（见 updateMentionAnchor）。
             宽度必须是 0，否则它自己会把后面的排版推开 */
          .comment-caret-mark {
            display: inline-block;
            width: 0;
            height: 1em;
          }

          /* 选人面板的定位锚点（见模板里 .mention-anchor 上的注释）。
             这个类落在 el-popover 的根节点上：它默认是个参与文档流的 inline <span>，
             不脱流的话会在输入框下面撑出一行空白。
             绝对定位 + 尺寸归零，就只剩"定位参照物"这一个作用；
             具体的 left / top 由 updateMentionAnchor 写成内联 style 覆盖掉这里的 0。
             popper 本体由 append-to-body 挂到 body 下，不受这里影响 */
          .mention-anchor {
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
          }

          /* 输入框里的 @ 高亮层。铺满整个输入框、盖在 textarea 上面 */
          .comment-input-mirror {
            @include comment-input-box;

            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            /* 盖在上面就必须放掉所有指针事件，否则点击和拖选都落在这层上，
               输入框既聚焦不了也选不中文字 */
            pointer-events: none;
            z-index: 1;
            /* 溢出的部分交给 syncMirrorScroll 用 scrollTop 对齐，
               这里不能是 auto/scroll —— 多一条滚动条就会占掉内容宽度 */
            overflow: hidden;
            color: var(--article-color);
            background: transparent;
            /* 镜像是纯展示层，别让它参与用户的复制选择（真实文字在下面那层） */
            user-select: none;

            /* @提及 用和评论正文里 .at-text 完全相同的蓝色，
               这样输入时和发出去之后是同一个颜色。
               这里不能像 .at-text 那样加 margin-right —— 一加就多出几个像素，
               后面所有文字连光标都会往右偏 */
            .mirror-at-text {
              color: #3d7eff;
            }
          }

          .comment-textarea {
            ::v-deep( .el-textarea__inner) {
              @include comment-input-box;

              color: var(--article-color) !important;
              border-radius: 4px;
              background-color: transparent;
              border: none;
              transition: all 0.25s ease-in-out 0s;
              resize: none;

              &:focus {
                background-color: #fff;
                border-color: #dcdfe6;
              }
            }

            ::v-deep( .el-input__count) {
              background: transparent;
              /* 镜像层是 z-index: 1，字数统计得压在它上面才不会被挡住 */
              z-index: 2;
            }

            /* 高亮生效时：真实文字让位给镜像层，textarea 只保留光标和选区。
               选区背景仍由 textarea 画，而镜像盖在上面，所以选中的文字照样看得见 */
            &.is-highlighting {
              ::v-deep( .el-textarea__inner) {
                color: transparent !important;
                caret-color: var(--article-color);
                /* 滚动条一出现就会占掉 textarea 的内容宽度，而镜像没有滚动条，
                   两层的折行位置立刻错开。索性把它藏掉：内容照样能滚
                  （滚轮、方向键、光标跟随），镜像由 syncMirrorScroll 跟着走，
                   剩下多少字有 .el-input__count 兜着 */
                scrollbar-width: none;

                &::-webkit-scrollbar {
                  width: 0;
                  height: 0;
                }
              }
            }
          }
        }

        /* 已选表情的预览（输入框下方、工具栏上方）。

           必须写在 .ml-3 这一层。原来这条规则被放到了下面 .commentwrap 里面
           （紧挨着 .comment-sticker-img，那是已发布评论上的表情），可主评论框的
           预览节点挂在 .comment-main > .comment-item > .box > .ml-3 下，根本不在
           .commentwrap 里 —— 加上 scoped 的 [data-v-x] 又借不到 Reply.vue 里那条
           同名规则，于是这个 img 一条样式都没命中，直接按原图尺寸铺开（表情图
           通常 400px 上下），连 × 也因为父级没有 position: relative 掉到图片下面。

           尺寸对齐旁边的 .comment-upload-item（72px），两种附件缩略图一样大；
           contain 而不是 cover：表情多为方形透明图，裁掉边缘会很难看。 */
        .comment-sticker-preview {
          position: relative;
          width: 72px;
          height: 72px;
          margin-top: 10px;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            border-radius: 6px;
            background: #f7f7f7;
            display: block;
          }

          .comment-upload-remove {
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.6);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 12px;

            &:hover {
              background: rgba(0, 0, 0, 0.8);
            }
          }
        }

        /* 已选配图的缩略图（输入框下方、工具栏上方）。
           这些是本地 blob 预览地址，发布时才真正上传到 COS —— 见 mixins/commentImageMixin.js */
        .comment-upload-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;

          .comment-upload-item {
            position: relative;
            width: 72px;
            height: 72px;
            border-radius: 6px;
            /* 删除按钮压在右上角外沿，所以这里不能 overflow: hidden，
               圆角改由里面的 img 自己带 */
            flex-shrink: 0;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              border-radius: 6px;
              display: block;
              background: #f2f3f5;
            }

            .comment-upload-remove {
              position: absolute;
              top: -6px;
              right: -6px;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: rgba(0, 0, 0, 0.6);
              color: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-size: 12px;

              &:hover {
                background: rgba(0, 0, 0, 0.8);
              }
            }
          }
        }

        .comment-btn {
          display: flex;
          align-items: center;
          margin: 10px 0;

          .emoji-btn {
            i {
              font-size: 1.3rem;
            }
          }

          .sticker-entry-btn {
            margin-left: 12px;
          }

          .sticker-pack-btn {
            margin-left: 10px;
            img {
              width: 22px;
              height: 22px;
              object-fit: contain;
              display: block;
            }
          }

          /* @ 按钮：iconfont 里没有现成的 @ 图标，直接用字符。
             字号和 .emoji-btn 的 1.3rem 对齐，视觉上两个按钮同高 */
          .mention-btn {
            margin-left: 12px;
            font-size: 1.3rem;
            font-weight: 600;
            line-height: 1;
            color: #606266;
            user-select: none;

            &:hover {
              color: #3d7eff;
            }
          }

          /* 配图按钮。el-upload 默认是 inline-block 且带自己的行高，
             不归零会让这一排按钮不等高 */
          .comment-image-upload {
            margin-left: 12px;
            line-height: 1;
          }

          .image-btn {
            font-size: 1.3rem;
            line-height: 1;
            color: #606266;

            &:hover {
              color: #3d7eff;
            }
          }

          .v-comment-btn {
            border: 1px solid #dcdfe6;
            border-radius: 4px;
            text-align: center;
            padding: 10px 16px;
            font-size: 14px;
            transition: all 0.3s;
            outline: none;
            background-color: #ffffff;
            color: #606266;
            
            &:hover {
              background-color: #f5f7fa;
              border-color: #c0c4cc;
            }
            
            &:active {
              background-color: #e8eaed;
            }
            
            &.has-content {
              border-color: #ff6b81;
              background-color: #ff6b81;
              color: #ffffff;
              
              &:hover {
                background-color: #ff8fa3;
                border-color: #ff8fa3;
              }
              
              &:active {
                background-color: #ff4757;
                border-color: #ff4757;
              }
            }
          }

          @media screen and (max-width: 767px) {
            .upload-btn {
              margin-left: auto;
              position: relative;
            }
          }

          @media screen and (min-width: 768px) {
            .upload-btn {
              margin-left: auto;
              overflow: hidden;
              transition: all 0.3s;
              position: relative;
            }
          }
        }
      }
    }
  }

  .commentwrap {
    width: 100%;
    padding: 0;
    list-style: none;

    /* 已发布评论上的表情。margin-left 对齐到头像右侧，与 .body / .comment-footer 一致。

       注意这里只有 .comment-sticker-img。输入框里那个 .comment-sticker-preview
       曾经也写在这一层，但预览节点挂在 .ml-3 下、不在 .commentwrap 里，规则永远
       命中不了，已移到 .ml-3 那一层（见上面）。 */
    .comment-sticker-img {
      width: 110px;
      height: 110px;
      object-fit: contain;
      display: block;
      margin: 8px 0 8px 58px;
      &.is-child {
        width: 88px;
        height: 88px;
        margin-left: 42px;
      }
    }

    /* 评论配图。
       写在 .commentwrap 这一层而不是各自的 .commentinfo 里面：父评论和回复的
       .commentinfo 是两条独立的嵌套链（.children 是 .comment 的兄弟节点，
       不是它的后代），写在里面就得复制两份，改一处漏一处。

       最多 3 张（后端 CommentImageUtils.MAX_COMMENT_IMAGE_COUNT），一行排完，
       不需要帖子配图那套响应式列数计算。

       固定正方形 + fit="cover"：评论区是纵向长列表，按原图比例撑高的话一张长图
       就能把它上下的评论顶出屏幕，翻评论变成翻图。裁掉的部分点开预览时是完整的。

       margin-left 与 .body / .comment-footer 一致，对齐到头像右侧。 */
    .comment-image-list {
      display: flex;
      flex-wrap: nowrap;
      gap: 8px;
      margin-left: 50px;
      margin-bottom: 12px;

      .comment-image {
        width: 84px;
        height: 84px;
        border-radius: 6px;
        overflow: hidden;
        cursor: zoom-in;
        background: #f2f3f5;
      }

      /* 回复里的图小一档，和「子评论比父评论小一号」的既有节奏一致 */
      &.is-child .comment-image {
        width: 70px;
        height: 70px;
      }
    }

    .comment-wrp {
      padding: 10px 0 16px 0;

      // 状态提示样式
      .comment-status-tip {
        text-align: center;
        padding: 20px;
        color: #999;
        border-top: none;
        
        &.empty-tip {
          padding: 40px 20px;
        }
        
        &.no-more-tip {
          border-top: none;
        }
      }
      
      // 当有状态提示时，确保最后一个评论项没有底部边框
      .ul-item:last-of-type {
        border-bottom: none;
      }

      .ul-item {
        clear: both;
        margin: 0;
        padding: 0;
        overflow: hidden;
        list-style: none;
        margin-bottom: 20px;
        border-bottom: 1px solid var(--border-line);

        &:last-child {
          padding-bottom: 0;
          border-bottom: none;
        }

        .left {
          .author {
            font-size: 24px;
            font-weight: 400;
            margin: 0;
            letter-spacing: 0px;
            text-transform: none;
            line-height: 20px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 4px;

            a {
              text-decoration: none;
              font-size: 14px;
              font-weight: 600;
              color: #82848a;
              transition: color 0.3s;

              &:not(.anonymous):hover {
                color: var(--theme-color);
              }

              &.anonymous {
                cursor: default;
                color: #999;
              }
            }

            .nick-type {
              display: inline-flex;
              align-items: center;
              margin-left: 2px;
            }

            .reply-to {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              font-size: 12px;
              color: #999;

              i {
                font-size: 12px;
              }

              .reply-nickname {
                color: var(--theme-color);
                font-weight: 500;
                transition: color 0.3s;

                &:not(.anonymous):hover {
                  color: #ff4757;
                }

                &.anonymous {
                  cursor: default;
                  color: #999;
                }
              }
            }
          }

          .school-name {
            font-size: 12px;
            color: #999;
            margin-top: 2px;
            margin-left: 0;
            display: flex;
            align-items: center;
            gap: 4px;
          }
        }

        .comment {
          width: 100%;
          padding-top: 10px;
          float: left;

          .main {
            float: right;
            width: 100%;
            padding: 0;

            &:last-child {
              border-bottom: 0;
            }

            .profile {
              float: left;
              margin-top: 6px;

              .avatar-link {
                display: inline-block;
                cursor: pointer;
                transition: opacity 0.3s;

                &:hover:not(.anonymous) {
                  opacity: 0.8;
                }

                &.anonymous {
                  cursor: default;
                }
              }
            }

            .commentinfo {
              .commeta {
                font-size: 16px;
                margin-bottom: 5px;
                text-transform: uppercase;
                color: #9499a8;
                margin-left: 50px;

                .comment-actions {
                  float: right;
                  display: flex;
                  align-items: center;
                  gap: 8px;
                }

                .comment-like-link {
                  cursor: pointer;
                  text-decoration: none;
                  font-size: 12px;
                  text-transform: uppercase;
                  color: #657786;
                  line-height: 20px;
                  padding: 0 6px;
                  border-radius: 3px;
                  transition: all 0.3s;
                  display: flex;
                  align-items: center;
                  gap: 4px;

                  i {
                    font-size: 14px;
                  }

                  &:hover {
                    color: #ff4757;
                  }

                  &.liked {
                    color: #ff4757;
                    
                    i {
                      animation: likeAnimation 0.3s ease;
                    }
                  }
                }

                @keyframes likeAnimation {
                  0% { transform: scale(1); }
                  50% { transform: scale(1.3); }
                  100% { transform: scale(1); }
                }

                .comment-reply-link {
                  cursor: pointer;
                  text-decoration: none;
                  font-size: 12px;
                  text-transform: uppercase;
                  color: #999;
                  background-color: transparent;
                  line-height: 20px;
                  padding: 0 6px;
                  border-radius: 3px;
                  transition: all 0.3s;

                  &:hover {
                    color: var(--theme-color);
                  }
                }

                .right {
                  .info {
                    margin-top: 2px;
                    font-size: 12px;
                    color: rgba(0, 0, 0, 0.35);

                    .comment-time {
                      display: inline-block;
                      margin-top: 6px;
                      font-size: 12px;
                      color: #657786;
                    }

                    .ipAddress {
                      color: #657786;
                    }
                  }
                }
              }

              .body {
                color: #63686d;
                position: relative;
                margin-bottom: 15px;
                margin-left: 50px;
                line-height: 1.6;
                white-space: pre-line;
                word-break: break-all;
                font-size: 15px !important;
                word-wrap: break-word;

                .markdown-content {
                  padding: 0;
                  white-space: pre-line;
                  word-break: break-all;
                  
                  .comment-text {
                    white-space: pre-wrap;
                    word-break: break-word;
                    margin: 0;
                    font-size: 15px;

                    /* 正文里的 @提及。
                       用蓝色而不是主题色：主题色在本项目里代表操作/强调，
                       而 @ 就是一个跳转链接，蓝色更符合用户既有认知（抖音/微博同样是蓝色）。
                       已注销的人不会渲染成 <a>，退化成普通文本，避免点进一个空主页。
                       字号/行高不写，继承 .comment-text，否则同一行两种字号混排行高会抖。
                       margin-right：连续 @ 两个人时把名字隔开，否则 "@甲 @乙" 挤在一起
                       分不清是一个人还是两个人（App 端同款处理）

                       hover 不加下划线：正文里连续 @ 几个人时，下划线会把
                       "@昵称" 和后面紧跟的空格一起划上，看着像名字多了一截；
                       而且 App 端本来就没有下划线，两端保持一致。
                       悬浮反馈改用淡一档的透明度，可点击这件事仍然传达得到。
                       hover 态显式再写一次 text-decoration: none，是为了挡住
                       全局或 Element UI 可能出现的 a:hover 下划线规则 */
                    .at-text {
                      color: #3d7eff;
                      text-decoration: none;
                      margin-right: 3px;

                      &:hover {
                        text-decoration: none;
                        opacity: 0.75;
                      }
                    }
                  }
                }
              }

              .comment-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-left: 50px;
                margin-top: 8px;
                padding-bottom: 16px;
                font-size: 12px;
                color: #657786;

                .info {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                }

                .comment-actions {
                  display: flex;
                  align-items: center;
                  gap: 16px;
                }

                .comment-like-link {
                  cursor: pointer;
                  text-decoration: none;
                  color: #657786;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  transition: all 0.3s;

                  i {
                    font-size: 14px;
                  }

                  &:hover {
                    color: #ff4757;
                  }

                  &.liked {
                    color: #ff4757;
                    
                    i {
                      animation: likeAnimation 0.3s ease;
                    }
                  }
                }

                .comment-reply-link {
                  cursor: pointer;
                  text-decoration: none;
                  color: #657786;
                  transition: all 0.3s;

                  &:hover {
                    color: var(--theme-color);
                  }
                }
              }
            }
          }
        }

        .children {
          padding: 0;
          list-style: none;
          margin-left: 40px;
          clear: both;

          // 去掉子评论区域所有边框
          * {
            border: none !important;
            border-bottom: none !important;
          }

          .comment-wrp {
            padding: 10px 0 16px 0;

            .expand-replies,
            .load-more-replies {
              padding: 10px 0;
              color: #1e3a5f;
              font-size: 13px;
              display: flex;
              align-items: center;
              gap: 6px;
              cursor: pointer;

              &:hover {
                color: var(--theme-color);
              }

              i {
                font-size: 14px;
              }
            }

            .ul-item {
              clear: both;
              margin: 0;
              padding: 0;
              overflow: hidden;
              list-style: none;
              margin-bottom: 16px;

              &:last-child {
                margin-bottom: 0;
              }

              .comment {
                width: 100%;
                padding-top: 10px;
                float: left;
                border: none;

                .main {
                  float: right;
                  width: 100%;
                  padding: 0;
                  border: none;

                  .profile {
                    float: left;
                    margin-top: 6px;

                    .avatar-link {
                      display: inline-block;
                      cursor: pointer;
                      transition: opacity 0.3s;

                      &:hover:not(.anonymous) {
                        opacity: 0.8;
                      }

                      &.anonymous {
                        cursor: default;
                      }
                    }
                  }

                  .commentinfo {
                    .commeta {
                      font-size: 16px;
                      margin-bottom: 5px;
                      text-transform: uppercase;
                      color: #9499a8;
                      margin-left: 50px;

                      .comment-actions {
                        float: right;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                      }

                      .comment-like-link {
                        cursor: pointer;
                        text-decoration: none;
                        font-size: 12px;
                        text-transform: uppercase;
                        color: #657786;
                        line-height: 20px;
                        padding: 0 6px;
                        border-radius: 3px;
                        transition: all 0.3s;
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        i {
                          font-size: 14px;
                        }

                        &:hover {
                          color: #ff4757;
                        }

                        &.liked {
                          color: #ff4757;
                          
                          i {
                            animation: likeAnimation 0.3s ease;
                          }
                        }
                      }

                      .comment-reply-link {
                        text-decoration: none;
                        font-size: 12px;
                        text-transform: uppercase;
                        color: #999;
                        background-color: transparent;
                        line-height: 20px;
                        padding: 0 6px;
                        border-radius: 3px;
                        transition: all 0.3s;

                        &:hover {
                          color: var(--theme-color);
                        }
                      }

                      .right {
                        .info {
                          margin-top: 2px;
                          font-size: 12px;
                          color: rgba(0, 0, 0, 0.35);

                          .comment-time {
                            display: inline-block;
                            margin-top: 6px;
                            font-size: 12px;
                            color: #657786;
                          }

                          .ipAddress {
                            color: #657786;
                          }
                        }
                      }
                    }

                    .body {
                      color: #63686d;
                      position: relative;
                      margin-bottom: 15px;
                      margin-left: 50px;
                      line-height: 1.6;
                      white-space: pre-line;
                      word-break: break-all;
                      font-size: 15px !important;
                      word-wrap: break-word;

                      .markdown-content {
                        padding: 0;
                        white-space: pre-line;
                        word-break: break-all;
                        
                        .comment-text {
                          font-size: 15px;

                          /* 子评论的 @提及，样式与父评论一致（含 hover 不加下划线） */
                          .at-text {
                            color: #3d7eff;
                            text-decoration: none;
                            margin-right: 3px;

                            &:hover {
                              text-decoration: none;
                              opacity: 0.75;
                            }
                          }
                        }
                      }
                    }

                    .comment-footer {
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      margin-left: 50px;
                      margin-top: 8px;
                      padding-bottom: 8px;
                      font-size: 12px;
                      color: #657786;
                      border-bottom: none;

                      .info {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                      }

                      .comment-actions {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                      }

                      .comment-like-link {
                        cursor: pointer;
                        text-decoration: none;
                        color: #657786;
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        transition: all 0.3s;

                        i {
                          font-size: 14px;
                        }

                        &:hover {
                          color: #ff4757;
                        }

                        &.liked {
                          color: #ff4757;
                          
                          i {
                            animation: likeAnimation 0.3s ease;
                          }
                        }
                      }

                      .comment-reply-link {
                        cursor: pointer;
                        text-decoration: none;
                        color: #657786;
                        transition: all 0.3s;

                        &:hover {
                          color: var(--theme-color);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
// 昵称选择器全局样式（因为 el-popover 挂载在 body 下，需要非 scoped 样式）
.nickname-popover {
  padding: 8px 0 !important;
  
  .nickname-list {
    max-height: 300px;
    overflow-y: auto;

    .nickname-item {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      transition: all 0.3s;
      border-radius: 4px;
      margin: 0 4px;

      &:hover {
        background-color: #f5f7fa;
      }

      &.active {
        background-color: #ecf5ff;
        
        .nickname-name {
          color: var(--theme-color);
          font-weight: 600;
        }
      }

      .nickname-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        margin-right: 12px;
        object-fit: cover;
      }

      .nickname-info {
        flex: 1;
        min-width: 0;

        .nickname-name {
          font-size: 14px;
          color: #303133;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .nickname-type {
          font-size: 12px;
          color: #909399;

          &.is-real {
            color: var(--theme-color);
          }

          &.is-anonymous {
            color: #909399;
          }
        }
      }

      .el-icon-check {
        color: var(--theme-color);
        font-size: 16px;
        font-weight: bold;
        margin-left: 8px;
      }
    }
  }
}

// ---------- 移动端：评论区收紧 ----------
// 手机屏幕上「4 行输入框 + 50px 缩进」会把评论列表挤到屏幕外，
// 这里把输入框压成 2 行、缩进按 34px 头像重算。
@media screen and (max-width: 767px) {
  .comment-main {
    .comment-item {
      margin: 4px 0 12px;
      border-radius: 12px;

      .box {
        padding: 8px;

        .avatar-wrapper .avatar img {
          width: 34px;
          height: 34px;
        }

        .ml-3 {
          margin-left: 8px !important;

          .comment-input {
            padding-right: 0;

            /* 这一整块 @media 在<b>非 scoped</b> 的 style 里（见本块开头），
               所以不能写 ::v-deep —— 它只在 scoped 块里会被编译掉，
               在这里会原样输出到产物，整条规则作为非法选择器被浏览器丢弃。
               原来这里写成了 .comment-textarea ::v-deep(.el-textarea__inner)，
               min-height 因此一直没生效过；非 scoped 块直接写目标选择器即可。

               另外这两条不合成一个选择器列表：一旦其中一个将来写错，
               非法选择器会把整条规则连带另一份声明一起废掉 */
            .comment-textarea .el-textarea__inner {
              min-height: 52px !important;
              padding: 8px 10px;
              font-size: 15px;
            }

            /* 移动端改小了字号和内边距，@ 高亮镜像和光标探针必须跟着改成一样的值：
               镜像有一项不一致就会错开成双影；探针不一致会把 "@" 的坐标量偏，
               选人面板跟错位置（见顶部 comment-input-box mixin） */
            .comment-input-mirror,
            .comment-caret-probe {
              padding: 8px 10px;
              font-size: 15px;
            }
          }

          .comment-btn {
            .v-comment-btn {
              padding: 7px 18px;
              border-radius: 16px;
            }
          }
        }
      }
    }

    .commentwrap .comment-wrp {
      padding: 4px 0 8px;

      .ul-item {
        margin-bottom: 8px;
      }

      .comment {
        padding-top: 8px;
      }

      // 头像 34px + 8px 间距，正文/元信息统一缩进 42px
      .commeta,
      .body,
      .comment-footer {
        margin-left: 42px !important;
      }

      .commeta {
        font-size: 14px !important;
      }

      .comment-text {
        font-size: 15px;
        line-height: 23px;
      }

      // 子评论缩进减半，避免多层回复后正文只剩一条窄缝
      .children {
        margin-left: 20px !important;
      }
    }
  }
}
</style>

